<script>
import Axios from 'axios';
import _ from 'lodash';
import _system from '@/libs/system';
import serverConfig from '../config.js';
import language from '../constants/language.js';
import sendIcon from '../assets/Vector.svg';
import { getCurrencySymbol, getCurrencyIcon } from '@/libs/currency-utils';
import ToolRenderer from '@/components/tools/ToolRenderer.vue';
import { templateUrl, openUrl } from '@/libs/system/toolsHelper';

// Swiss arabian pageIds
const SPECIAL_ORDER_TRACKING_PAGE_IDS = [
  '68625484dde2a175215d5b8a',
  '68ad4373ecec34bc4d33b6ce',
  '68ad42fdecec34bc4d33b685',
  '68ad403eecec34bc4d33b4c7',
  '68ad4286ecec34bc4d33b626',
  '68ad444fecec34bc4d33b753'
];

export default {
  name: 'Widget',
  directives: {
    trackLinks: {
      mounted(el, binding, vnode) {
        const links = el.querySelectorAll('a');
        links.forEach((link) => {
          const url = link.getAttribute('href');
          if (url && url.includes('/products/') && !url.includes('/collections/') && !url.includes('/pages/')) {
            link.addEventListener('click', (event) => {
              const viewModel = vnode.context || vnode.componentInstance || binding.instance;
              if (viewModel && typeof viewModel.trackLinkClick === 'function') {
                viewModel.trackLinkClick(url);
              } else {
                console.error('Could not access trackLinkClick method');
              }
            });
          }
        });
      },
      updated(el, binding, vnode) {
        // Re-apply the same logic when content updates
        const links = el.querySelectorAll('a');
        links.forEach((link) => {
          const url = link.getAttribute('href');
          if (url && !url.includes('/products/') && !url.includes('/collections/') && !url.includes('/pages/')) {
            // Remove old listeners first to prevent duplicates
            link.removeEventListener('click', () => {});
            link.addEventListener('click', (event) => {
              const vm = vnode.context || vnode.componentInstance || binding.instance;
              if (vm && typeof vm.trackLinkClick === 'function') {
                vm.trackLinkClick(url);
              } else {
                console.error('Could not access trackLinkClick method');
              }
            });
          }
        });
      },
    },
  },
  data() {
    return {
      text: '',
      productName: '',
      storeId: '',
      browserLang: '',
      messages: [],
      linksObject: [],
      pendingFiles: [],
      pendingUploadedImages: [],
      messageDetails: [],
      botConfig: {},
      productUrl: '',
      dropOffProductJson: null,
      retrainContext: null,
      engagement: null,
      local: false,
      testing: false,
      juphy: false,
      report: false,
      isChatOpen: false,
      onRetrain: false,
      extension: false,
      inputClickedTwice: false,
      headerVisibility: true,
      sendingToCartSet: new Set(),
      addedToCartSet: new Set(),
      isMobileFullscreen: true,
      parentWindowWidth: null,
      conversationProductSummarry: null,
      recommendedProducts: [],
      contentLang: {
        submitButton: 'Submit',
        orderTrackingPlaceHolder: '#12345',
        productDetailsLink: 'View Details',
        collectionDetailsLink: 'View Collection',
        alwaysActive: 'Always Active',
        textPlaceholder: 'Type a message...',
        typing: 'typing',
        addToCartText: 'Add',
      },
      orderStatus: {
        email: '',
        number: '',
      },
      website: {
        name: '',
        pageId: '',
        visitorId: '',
      },
      loading: {
        typing: false,
      },
      keepingAsking: false,
      activeThreadId: null,
      activeRepliedId: null,
      onTheLine: [],
      disclaimerText: `By continuing, you understand and agree that the contents of this chat may be recorded by us or by third parties working with us, and used as described in our <a href="https://shop.redline360.com/policies/privacy-policy" target="_blank">Privacy Notice</a>. If you do not agree, please do not use our chat feature.`,
      toolsRegistry: {},
      visitorLanguage: null,
    };
  },
  computed: {
    specialOrderTrackingPageIds() {
      return SPECIAL_ORDER_TRACKING_PAGE_IDS;
    },
    poweredByJuphyColor() {
      const brightness = this.calculateBrightness(this.botConfig.style.middle.backgroundColor);
      return brightness < 80
        ? {
            backgroundColor: this.botConfig.style.middle.backgroundColor,
            color: 'rgba(255,255,255,0.9)',
            opacity: '0.35',
          }
        : { backgroundColor: this.botConfig.style.middle.backgroundColor, color: 'rgba(0,0,0,0.8)', opacity: '0.35' };
    },
    filteredActionButtons() {
      if (!this.botConfig.actionButtons || !Array.isArray(this.botConfig.actionButtons)) {
        return [];
      }

      // Try to find action buttons matching browserLang
      const byBrowserLang = this.botConfig.actionButtons.filter((btn) => btn.lang === this.browserLang);
      if (byBrowserLang.length > 0) {
        return byBrowserLang;
      }

      // Fallback: use actionButtonPrimaryLanguage
      const primaryLang = this.botConfig.actionButtonPrimaryLanguage;
      if (primaryLang) {
        const byPrimaryLang = this.botConfig.actionButtons.filter((btn) => btn.lang === primaryLang);
        if (byPrimaryLang.length > 0) {
          return byPrimaryLang;
        }
      }
      return [];
    },
    isMobile() {
      // Use parent window width if available (from juphy.js), otherwise fallback to iframe width
      const width = this.parentWindowWidth !== null ? this.parentWindowWidth : window.innerWidth;
      return width <= 768;
    },
  },
  components: { ToolRenderer },
  methods: {
    getCurrencyIcon,
    getCurrencySymbol,
    addToCart(message) {
      this.sendingToCartSet.add(message.variantId);
      window.parent.postMessage({ action: 'addToCart', data: message.variantId }, '*');
    },
    handleLiveChatClick() {
      window.parent.postMessage({ action: 'juphyLiveChatButtonClicked' }, '*');
    },
    scrollCarousel(event, direction) {
      const carousel = event.target.closest('.richMsg-wrapper')?.querySelector('.richMsg');
      if (!carousel) return;
      const scrollAmount = 260;
      carousel.scrollBy({ left: direction * scrollAmount, behavior: 'smooth' });
    },
    updateCarouselArrows(event) {
      const carousel = event?.target || event;
      const wrapper = carousel.closest('.richMsg-wrapper');
      if (!wrapper) return;
      const leftArrow = wrapper.querySelector('.carousel-arrow-left');
      const rightArrow = wrapper.querySelector('.carousel-arrow-right');
      if (leftArrow) leftArrow.classList.toggle('hidden', carousel.scrollLeft <= 0);
      if (rightArrow) rightArrow.classList.toggle('hidden', carousel.scrollLeft >= carousel.scrollWidth - carousel.clientWidth - 1);
    },
    initCarouselArrows() {
      this.$nextTick(() => {
        const root = this.$el && this.$el.nodeType === 1 ? this.$el : document;
        const carousels = root.querySelectorAll('.richMsg:not(.order)');
        if (carousels) carousels.forEach((c) => this.updateCarouselArrows(c));
      });
    },
    isButtonLoading(variantId) {
      return this.sendingToCartSet.has(variantId);
    },
    isCartButtonAdded(variantId) {
      return this.addedToCartSet.has(variantId);
    },
    initializeWidget() {
      this.local = this.$route.query.local;
      this.testing = this.$route.query.testing;

      if (
        this.botConfig.hasJuphyBranding === null ||
        this.botConfig.hasJuphyBranding === undefined
      ) {
        this.botConfig.hasJuphyBranding = true;
      }

      // Set initial mobile fullscreen state (only on mobile devices)
      if (this.isMobile) {
        if (this.botConfig.widget_fullscreen !== undefined) {
          this.isMobileFullscreen = this.botConfig.widget_fullscreen;
        } else {
          this.isMobileFullscreen = true; 
        }
      }

      if (this.$route.query.local) {
        if (this.$route.query.testing) {
          //    this.botConfig = JSON.parse(decodeURIComponent(new URLSearchParams(window.location.search).get('config')));

          if (this.messages.length <= 0 && this.botConfig.welcome && this.botConfig.welcome.length) {
            this.messages = [
              {
                text: this.botConfig.welcome,
                type: 'bot',
              },
            ];
          }

          if (this.botConfig.actionButtons) {
            this.messages.push({ actionButton: true, type: 'agent' });
          }

          const pageId = new URLSearchParams(window.location.search).get('juphy_widget_token');
          const visitorId = new URLSearchParams(window.location.search).get('juphy_visitor_token');
          const brand_name = new URLSearchParams(window.location.search).get('brand_name');
          const juphy = new URLSearchParams(window.location.search).get('juphy');
          const report = new URLSearchParams(window.location.search).get('report');
          const currentUrl = new URLSearchParams(window.location.search).get('current_url');
          const onRetrain = new URLSearchParams(window.location.search).get('onRetrain');
          const storeId = new URLSearchParams(window.location.search).get('storeId');
          const extension = new URLSearchParams(window.location.search).get('extension') === 'true';

          this.website.pageId = pageId;
          this.website.visitorId = visitorId;
          this.website.name = brand_name;
          this.juphy = juphy;
          this.currentUrl = currentUrl;
          this.report = report;
          this.onRetrain = onRetrain;
          this.storeId = storeId;
          this.extension = extension;
        } else {
          this.botConfig = {
            name: 'AI Shopping Assistant',
            welcome: 'Hello 👋, How can I help you today?',
            position: 'right',
            style: {
              name: "Juphy's Light",
              header: { backgroundColor: '#f6f6f6', name: { textColor: '#202325' }, status: { textColor: '#72777A' } },
              middle: {
                backgroundColor: '#ffffff',
                bot: { backgroundColor: '#f2f4f5', textColor: '#ffffff' },
                agent: { backgroundColor: '#0070f0', textColor: '#ffffff' },
              },
              footer: {
                backgroundColor: '#f6f6f6',
                input: { backgroundColor: '#ffffff', borderColor: '#DDDDDD', textColor: '#222222' },
                button: { backgroundColor: '#444444', textColor: '#ffffff' },
              },
            },
          };

          if (this.messages.length <= 0) {
            this.messages = [
              {
                text: this.botConfig.welcome,
                type: 'bot',
              },
            ];
          }
          //this.website.pageId = "66014eff6ce1c46218dbc7b7"
          this.website.pageId = '658120e1184f8ced4240d5c6';
          this.website.name = 'Evergreen Local Test';
          //this.website.pageId = "65eabfcec144304ae2b66ba7"
          //this.website.name = "Hofsilk Local Test";
          this.website.visitorId = 'pYY3CQMAhTits7tilJpz';
        }

        /*       if(this.juphy){
      window.addEventListener('message', (event) => {
        if(event.data.data && event.type === 'message'){
          let data = event.data.data;
          this.botConfig = data.config;
          _.get(this.messages, '0').text = data.config.welcome;
        }
      });
    } */
        window.addEventListener('message', this.handleMessage);
      } else {
        //    this.botConfig = JSON.parse(decodeURIComponent(new URLSearchParams(window.location.search).get('config')));
        if (this.messages.length <= 0 && this.botConfig.welcome && this.botConfig.welcome.length) {
          this.messages = [
            {
              text: this.botConfig.welcome,
              type: 'bot',
            },
          ];
        }

        if (this.botConfig.actionButtons) {
          this.messages.push({ actionButton: true, type: 'agent' });
        }

        const pageId = new URLSearchParams(window.location.search).get('juphy_widget_token');
        const visitorId = new URLSearchParams(window.location.search).get('juphy_visitor_token');
        const brand_name = new URLSearchParams(window.location.search).get('brand_name');
        const juphy = new URLSearchParams(window.location.search).get('juphy');
        const report = new URLSearchParams(window.location.search).get('report');
        const currentUrl = new URLSearchParams(window.location.search).get('current_url');
        const currentPageUrl = new URL(currentUrl);
        const crossSelling = new URLSearchParams(currentPageUrl.search).get('crossSelling');
        const onRetrain = new URLSearchParams(window.location.search).get('onRetrain');
        const storeId = new URLSearchParams(window.location.search).get('storeId');
        const extension = new URLSearchParams(window.location.search).get('extension') === 'true';

        this.website.pageId = pageId;
        this.website.visitorId = visitorId;
        this.website.name = brand_name;
        this.juphy = juphy;
        this.report = report;
        this.currentUrl = currentUrl;
        this.onRetrain = onRetrain;
        this.storeId = storeId;
        this.extension = extension;
        //lang update for camilleboutiqueparis.com
        if (this.website.pageId == '66c7386f49a2e509623965b0') {
          this.contentLang.submitButton = 'Envoyer';
          this.contentLang.orderTrackingPlaceHolder = 'Numéro de commande';
          this.contentLang.productDetailsLink = 'Voir les détails';
          this.contentLang.collectionDetailsLink = 'Voir la collection';
          this.contentLang.alwaysActive = 'Toujours en ligne';
          this.contentLang.textPlaceholder = 'Écrire un message...';
          this.contentLang.typing = 'écrit';
          this.contentLang.addToCartText = 'Ajouter au panier';
        }

        if (this.website.pageId == '67432e4d75f816b43d80a949') {
          this.contentLang.submitButton = 'Enviar';
          this.contentLang.orderTrackingPlaceHolder = 'Número de pedido';
          this.contentLang.productDetailsLink = 'Ver detalles';
          this.contentLang.collectionDetailsLink = 'Ver colección';
          this.contentLang.alwaysActive = 'Siempre en línea';
          this.contentLang.textPlaceholder = 'Escribe un mensaje...';
          this.contentLang.typing = 'escribiendo';
          this.contentLang.addToCartText = 'Añadir al carrito';
        }

        if (this.website.pageId == '66bde1b649a2e50962adfe92') {
          this.contentLang.submitButton = 'Gönder';
          this.contentLang.orderTrackingPlaceHolder = 'Sipariş numarası';
          this.contentLang.alwaysActive = 'Çevrimiçi';
          this.contentLang.textPlaceholder = 'Bir mesaj yazın...';
          this.contentLang.productDetailsLink = 'Ürünü incele';
          this.contentLang.collectionDetailsLink = 'Koleksiyonu görüntüle';
          this.contentLang.typing = 'yazıyor';
          this.contentLang.addToCartText = 'Sepete ekle';
        }

        if (this.website.pageId == '6790993b2d9ea1ce9a887172') {
          this.contentLang.submitButton = 'Send';
          this.contentLang.orderTrackingPlaceHolder = 'Ordrenummer';
          this.contentLang.alwaysActive = 'Pålogget';
          this.contentLang.textPlaceholder = 'Skriv en melding...';
          this.contentLang.productDetailsLink = 'Se produktet';
          this.contentLang.collectionDetailsLink = 'Se kolleksjonen';
          this.contentLang.typing = 'skriver';
          this.contentLang.addToCartText = 'Tilføj til kurv';
        }

        if (SPECIAL_ORDER_TRACKING_PAGE_IDS.includes(this.website.pageId)) {
          this.contentLang.orderTrackingPlaceHolder = 'Tracking number';
        }

        /*         const pageIds = [
          '664c827a9528e27626686061', // uk-water-filters
          '67365afd75f816b43d570115', // jokers
          '67179bdecf536ae3451183a1', // misela
          '674b3c168d80eb36d91fb651', // dailybeauty
        ];

        if (pageIds.includes(this.website.pageId)) {
          this.handleEngagement('pageView');
        } else if (crossSelling && !this.juphy && visitorId) {
          this.handleEngagement('crossSelling');
        } else if (this.engagement && !this.juphy && visitorId) {
          this.handleEngagement('pageView');
        } */

        // Replace the long event listener with:
        window.addEventListener('message', this.handleMessage);

        window.addEventListener('message', async (event) => {
          if (event.data && event.data.action === 'sendProductUrl') {
            let productUrl = event.data.data;

            if (this.productUrl === productUrl) {
              return;
            }

            this.productUrl = event.data.data;

            try {
              const response = await fetch(productUrl + '.json');

              const data = await response.json();
              this.dropOffProductJson = data.product;
            } catch (error) {
              console.error('Error fetching product data:', error);
            }
          }
        });

        let previousConversationData = localStorage.getItem('juphy_previous_conversation');

        if (!this.juphy && previousConversationData) {
          previousConversationData = JSON.parse(previousConversationData);

          if (
            previousConversationData &&
            Object.keys(previousConversationData).every((key) => previousConversationData[key]) &&
            Date.now() - previousConversationData.createdAt < 10 * 60 * 1000 &&
            (this.juphy || previousConversationData.baseUrl === this.getCurrentBaseUrl())
          ) {
            this.messages = previousConversationData.messages;
            this.activeThreadId = previousConversationData.activeThreadId;
            this.activeRepliedId = previousConversationData.activeRepliedId;
            if (
              previousConversationData.multipleLinkPreviews &&
              previousConversationData.multipleLinkPreviews.length > 0
            ) {
              this.linkPreviewGeneratorJsonMultiple(previousConversationData.multipleLinkPreviews);
            }
            setTimeout(() => {
              document.getElementById('wcw_messages_container').scrollTo({
                top: document.getElementById('wcw_messages_container').scrollHeight,
                behavior: 'smooth',
              });
            }, 100);
          }
        }
      }
    },
    startFollowUp: _.debounce(function () {
      let payload = {
        body: {
          context: this.messages,
        },
      };

      _system.services.chatbot.getFollowUpText(payload, (err, response) => {
        if (err) {
          console.error('Follow up error:', err);
          return;
        }

        if (response && response.trim().length > 0 && this.isChatOpen && !this.text.length) {
          this.messages.push({
            text: response,
            type: 'bot',
          });
          setTimeout(() => {
            document.getElementById('wcw_messages_container').scrollTo({
              top: document.getElementById('wcw_messages_container').scrollHeight,
              behavior: 'smooth',
            });
          }, 100);
        }
      });
    }, 45000),
    handleInputClick(event) {
      if (!document.hasFocus()) {
        window.focus();
      }
      if (this.isMobile) {
        setTimeout(() => {
          this.inputClickedTwice = true;
          this.$refs.inputField.blur();
          this.$refs.inputField.focus();
          this.$refs.inputField.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 200);
      }
      this.$refs.inputField.focus();
    },
    pushIntoLine() {
      if (!this.text.length && (!this.pendingFiles || this.pendingFiles.length === 0) && (!this.pendingUploadedImages || this.pendingUploadedImages.length === 0)) return '';

      const trimmed = this.text.trim();

      const hasActionButton = this.messages.some((message) => 'actionButton' in message);
      if (hasActionButton) {
        this.messages = this.messages.filter((message) => !('actionButton' in message));
      }

      // Check if disambiguation buttons were active (user is answering a question)
      const isQuickAnswer = this.messages.some((msg) => msg.potentialAnswers && msg.potentialAnswers.length);

      // Clear any disambiguation quick answer buttons
      this.messages.forEach((msg) => {
        if (msg.potentialAnswers) {
          msg.potentialAnswers = [];
        }
      });

      // Upload queued files first, then combine with text in a single message
      const filesToUpload = (this.pendingFiles || []).slice();
      const uploadPromises = filesToUpload.map((f) => new Promise((resolve) => {
        const formData = new FormData();
        formData.append('file', f.file);
        _system.services.cloudflare.uploadImage({ formData }, (error, response) => {
          if (error || !response?.permanentUrl) return resolve(null);
          resolve({ url: response.permanentUrl, name: f.name, size: f.size });
        });
      }));

      Promise.all(uploadPromises).then((uploaded) => {
        // Collect all image URLs
        const allImages = [];
        
        uploaded.filter(Boolean).forEach((u) => {
          allImages.push(u.url);
        });

        (this.pendingUploadedImages || []).forEach((img) => {
          allImages.push(img.url);
        });

        // If we have both images and text, combine them in a single bubble
        if (allImages.length > 0 && trimmed.length) {
          // Build combined text with image URLs
          const imageTexts = allImages.map(url => `img: ${url}`).join('\n');
          const combinedText = `${imageTexts}\n ${trimmed}`;
          
          // Show in UI as a combined message
          this.messages.push({ 
            text: combinedText, 
            type: 'agent', 
            object: {},
            images: allImages,
            hasImages: true,
            ...(isQuickAnswer && { quickAnswer: true }),
          });
          
          // Queue only once to backend with combined text
          this.onTheLine.push({ text: combinedText, type: 'agent', object: {}, ...(isQuickAnswer && { quickAnswer: true }) });
        } 
        // If we only have images (no text)
        else if (allImages.length > 0) {
          allImages.forEach((url) => {
            this.messages.push({ text: url, type: 'agent', image: true, imageUrl: url });
            this.onTheLine.push({ text: `img: ${url}`, type: 'agent', object: {} });
          });
        }
        // If we only have text (no images)
        else if (trimmed.length) {
          this.messages.push({ text: trimmed, type: 'agent', object: {}, ...(isQuickAnswer && { quickAnswer: true }) });
          this.onTheLine.push({ text: trimmed, type: 'agent', object: {}, ...(isQuickAnswer && { quickAnswer: true }) });
        }

        if (!this.keepingAsking && this.onTheLine.length) {
          this.keepingAsking = true;
          this.startRun(this.activeThreadId, this.activeRepliedId);
        }

        this.text = '';
        this.pendingFiles = [];
        this.pendingUploadedImages = [];

        this.$nextTick(() => {
          const c = document.getElementById('wcw_messages_container');
          if (c) c.scrollTo({ top: c.scrollHeight, behavior: 'smooth' });
        });
      });
    },
    startRun(threadId, replied) {
      this.loading.typing = true;
      this.isChatOpen = true;

      // Scroll to bottom immediately when typing starts
      this.$nextTick(() => {
        const container = document.getElementById('wcw_messages_container');
        if (container) {
          container.scrollTo({
            top: container.scrollHeight,
            behavior: 'smooth',
          });
        }
      });

      let content = '';

      if (this.retrainContext) {
        content = `${this.retrainContext} ${_.first(this.onTheLine).text}`;
        this.retrainContext = null;
      } else {
        content = _.first(this.onTheLine).text;
      }

      if (!this.engagement && !this.juphy && this.website.visitorId) {
        this.handleEngagement(content);
      }

      // Convert tool messages to text format so AI knows what tools were called
      const processedMessages = this.messages.map(msg => {
        if (msg.type === 'tool' && msg.tool) {
          // Convert tool message to a bot message with toolCalled metadata
          const toolName = msg.tool.name || msg.tool.type;
          const instructionText = msg.instructionText || '';
          
          return {
            type: 'bot',
            text: instructionText || 'Tool was presented to user.',
            toolCalled: toolName  // Clean metadata about which tool was called
          };
        }
        return msg;
      });

      let askPayload = {
        content: content,
        messages: processedMessages,
        activeThreadId: this.activeThreadId,
        activeRepliedId: this.activeRepliedId,
        productsContextSummary: this.conversationProductSummarry || null,
        recommendedProducts: this.recommendedProducts || [],
      };

      if (this.dropOffProductJson) {
        askPayload.product = this.dropOffProductJson;
      }

      if (this.website.visitorId) {
        askPayload.visitorId = this.website.visitorId;
      }

      if (this.productName) {
        askPayload.content = `${askPayload.content}  ${this.productName}`;
        this.productName = '';
      }

      // TODO: Remove this after testing
      //askPayload.storeName = "cascadian.store"

      let payload = {
        query: {
          pageId: this.website.pageId,
          threadId: threadId,
          replied: replied,
          local: this.$route.query.local,
        },
        body: askPayload,
      };

      _system.services.chatbot.askQuestion(payload, (err, response) => {
        if (err) {
          console.error('Error on Message Create:', err);
          this.loading.typing = false;
          return;
        }

        let threadAndRunObject = response;

        this.activeThreadId = _.get(response, 'message.thread_id', this.activeThreadId);
        //this.dropOffProductJson = null

        this.messageDetails = response.messages;

        //Check if the text has @ and # in the string with .includes()
        let isLastMessageOrderMessage = _.chain(response)
          .get('messages')
          .last()
          .get('text', '')
          .thru((text) => (text.includes('@') && text.includes('#') ? true : false))
          .value();

        if (isLastMessageOrderMessage) {
          var lastMessage = _.last(_.get(response, 'messages')).text;

          //Temporary upgrade for user camilleboutiqueparis.com
          if (this.website.pageId == '66c7386f49a2e509623965b0' || this.website.pageId == '66c6194249a2e50962047946') {
            lastMessage = _.last(_.get(response, 'messages')).text.replace('#', 'AM');
          }

          this.messages.push({
            text: lastMessage,
            type: 'bot',
            order: true,
            wholeMessageForBackend: _.last(_.get(response, 'messages')),
          });

          this.loading.typing = false;

          setTimeout(() => {
            document.getElementById('wcw_messages_container').scrollTo({
              top: document.getElementById('wcw_messages_container').scrollHeight,
              behavior: 'smooth',
            });
          }, 100);

          //Remove the processed message from queue
          this.onTheLine = _.tail(this.onTheLine);

          return (this.keepingAsking = false);
        }
        //End of Order Message

        //Regenerate reply for the URLs

        let answerString = _.get(_.last(_.get(response, 'messages')), 'text', '');

        // Handle tool_call passthrough from backend when type !== function
        if (response.toolCall && response.toolCall.function) {
          if (!Object.keys(this.toolsRegistry).length) this.fetchTools();
          this.renderToolMessage(response.toolCall);
        }

        let multipleLinkPreviews = response.links || [];
        let visitorLanguage = response.visitorLanguage || null;

        this.conversationProductSummarry = response.productsContextSummary || null;
        this.recommendedProducts = response.recommendedProducts || [];
        this.visitorLanguage = visitorLanguage;

        // quick answers from backend
        let potentialAnswers = response.potentialAnswers || [];

        // Only render bot answer if backend provided an AI answer (no toolCall-only case)
        if (!response.toolCall) {
          this.messages.push({
            text: answerString,
            type: 'bot',
            wholeMessageForBackend: _.last(_.get(response, 'messages')),
            potentialAnswers: potentialAnswers,
          });
        }
        //this.startFollowUp();
        window.parent.postMessage(
          {
            action: 'juphyEngagementStart',
            origin: window.location.origin,
            data: this.activeThreadId,
          },
          '*'
        );
        this.loading.typing = false;
        //Scroll Down after Render
        setTimeout(() => {
          document.getElementById('wcw_messages_container').scrollTo({
            top: document.getElementById('wcw_messages_container').scrollHeight,
            behavior: 'smooth',
          });
        }, 100);

        if (multipleLinkPreviews.length) {
          this.linkPreviewGeneratorJsonMultiple(multipleLinkPreviews);
        }

        //Remove the processed message from queue
        this.onTheLine = _.tail(this.onTheLine);

        //Check if there are more messages in queue
        if (this.onTheLine.length) {
          const nextThreadId = _.get(threadAndRunObject, 'message.thread_id', this.activeThreadId);
          const nextRepliedId = _.chain(response).get('messages').last().value().id;
          this.startRun(nextThreadId, nextRepliedId);
        } else {
          //No message
          this.activeRepliedId = _.chain(response).get('messages').last().value().id;
          this.keepingAsking = false;
          let allConversationData = {
            messages: this.messages,
            activeThreadId: this.activeThreadId,
            activeRepliedId: this.activeRepliedId,
            createdAt: Date.now(),
            multipleLinkPreviews: multipleLinkPreviews,
          };

          if (!this.juphy) {
            allConversationData.baseUrl = this.getCurrentBaseUrl();
          }

          localStorage.setItem('juphy_previous_conversation', JSON.stringify(allConversationData));
        }

        let eventPayload = {
          type: 'juphy',
          name: 'widget_message_sent',
          data: {
            message: askPayload.content,
          },
        };

        if (this.currentUrl && !this.currentUrl.includes('https://admin.shopify.com/store/')) {
          this.sendEngagementLogV2(eventPayload);
        }
      });
    },
    linkPreviewGeneratorJsonMultiple(urlsArrayObject) {

      let payload = {
        urls: urlsArrayObject,
        visitorLanguage: this.visitorLanguage,
      };

      _system.services.chatbot.getShopifyProductsMultiple(payload, (err, results) => {
        if (err) {
          console.error('Error:', err);
          this.loading.typing = false;
          return;
        }

        // Process the results the same way as before
        let messages = results.map((result) => {
          if (result.error || !result.data) {
            return null;
          }

          let urlObject = result.urlObject;
          let metaData = result.data;

          let isCollection = urlObject.url.includes('/collections') && !urlObject.url.includes('/products');
          let dataKey = isCollection ? 'collection' : 'product';
          let variantId = _.get(metaData, `${dataKey}.variants[0].id`, '');
          let title = _.get(metaData, `${dataKey}.title`, '').substring(0, 28);
          let description = new DOMParser()
            .parseFromString(
              _.get(metaData, `${dataKey}.${isCollection ? 'description' : 'body_html'}`, '') + '...',
              'text/html'
            )
            .body.textContent.substring(0, 28);
          let image = _.get(metaData, `${dataKey}.image.src`, '');
          let price = _.get(metaData, `${dataKey}.variants[0].price`, null);
          const currency = _.get(metaData, `${dataKey}.variants[0].price_currency`, null);
          const currencySymbol = getCurrencySymbol(currency);

          if (!image) {
            return null;
          }

          let message = {
            title: title,
            desc: description || '',
            image: image,
            url: urlObject.refUrl,
            rich: true,
            multiple: true,
            price: price,
            currency: currency,
            currencySymbol: currencySymbol,
            compareAtPrice: _.get(metaData, `${dataKey}.variants[0].compare_at_price`, null),
            type: 'bot',
            isCollection: isCollection,
            variantId: variantId,
          };
          return message;
        });

        let compactedArray = _.compact(messages).sort((a, b) => {
          if (a.isCollection && !b.isCollection) return -1;
          if (!a.isCollection && b.isCollection) return 1;
          return 0;
        });

        if (compactedArray.length) {
          this.messages.push({ rich: true, type: 'bot', multiple: true, messages: compactedArray });
          this.loading.typing = false;

          setTimeout(() => {
            document.getElementById('wcw_messages_container').scrollTo({
              top: document.getElementById('wcw_messages_container').scrollHeight,
              behavior: 'smooth',
            });
          }, 100);
        } else {
          this.loading.typing = false;
        }
      });
    },
    setWidgetLanguage(selectedLanguage) {
      if (selectedLanguage === 'auto') {
        this.browserLang = (navigator.language || navigator.userLanguage || 'en').split('-')[0].toLowerCase();
        this.contentLang = language[this.browserLang] || language['en'];
      } else {
        this.contentLang = language[selectedLanguage] || language['en'];
      }
      if (SPECIAL_ORDER_TRACKING_PAGE_IDS.includes(this.website.pageId)) {
        this.contentLang.orderTrackingPlaceHolder = 'Tracking number';
      }
    },
    generateRandomString(length) {
      var result = '';
      var characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
      for (var i = 0; i < length; i++) {
        result += characters.charAt(Math.floor(Math.random() * characters.length));
      }
      return result;
    },
    generateEventId() {
      const timestamp = Date.now();
      const random = this.generateRandomString(8);
      return `${timestamp}-${random}`;
    },

    linkPreviewGeneratorJson(url) {
      this.loading.typing = true;

      const urlObj = new URL(url);
      const baseUrl = urlObj.origin + urlObj.pathname + '.json';
      
      // Build localized URL if visitorLanguage is set
      let localizedUrl = null;
      if (this.visitorLanguage) {
        const pathWithoutLeadingSlash = urlObj.pathname.replace(/^\//, '');
        localizedUrl = urlObj.origin + '/' + this.visitorLanguage + '/' + pathWithoutLeadingSlash + '.json';
      }

      const handleProductData = (metaData) => {
        setTimeout(() => {
          const currency = _.get(metaData, 'data.product.variants[0].price_currency', null);
          this.messages.push({
            title: _.get(metaData, 'data.product.title', ''),
            desc:
              new DOMParser().parseFromString(
                _.get(metaData, 'data.product.body_html', '').slice(0, 100) + '...',
                'text/html'
              ).body.textContent || '',
            image: _.get(metaData, 'data.product.image.src', ''),
            variantId: _.get(metaData, 'data.product.variants[0].id', ''),
            url: url,
            rich: true,
            type: 'bot',
            price: _.get(metaData, 'data.product.variants[0].price', null),
            currency: currency,
            currencySymbol: getCurrencySymbol(currency),
            compareAtPrice: _.get(metaData, 'data.product.variants[0].compare_at_price', null),
          });

          this.loading.typing = false;

          setTimeout(() => {
            document.getElementById('wcw_messages_container').scrollTo({
              top: document.getElementById('wcw_messages_container').scrollHeight,
              behavior: 'smooth',
            });
          }, 100);
        }, 500);
      };

      const fetchUrl = localizedUrl || baseUrl;
      
      Axios.get(fetchUrl)
        .then(handleProductData)
        .catch((error) => {
          // If localized URL failed, try default URL as fallback
          if (localizedUrl) {
            Axios.get(baseUrl)
              .then(handleProductData)
              .catch((fallbackError) => {
                console.error('Error:', fallbackError);
                this.loading.typing = false;
              });
          } else {
            console.error('Error:', error);
            this.loading.typing = false;
          }
        });
    },
    close() {
      this.isChatOpen = false;
      window.parent.postMessage(
        {
          action: 'close-iframe',
          origin: window.location.origin,
        },
        '*'
      );
    },
    toggleMobileHeight() {
      if (this.isMobile) {
        this.isMobileFullscreen = !this.isMobileFullscreen;
      }
    },
    retrainMessage(answer, question, index) {
      let threadId = this.activeThreadId;
      let messageObject = _.chain(this.messageDetails)
        .filter((msg) => _.isString(msg.text))
        .find((msg) => msg.text.includes(answer.slice(0, 50)))
        .value();
      let messageId = messageObject ? messageObject.id : null;

      const history = JSON.parse(JSON.stringify(this.messages.slice(0, index + 1)));

      const agentContextMessages = _.chain(this.messages)
        .filter((message) => message.type === 'agent')
        .thru((messages) => {
          const filteredIndex = _.findIndex(messages, (msg) => msg.text === this.messages[index - 1].text);
          return _.slice(messages, Math.max(filteredIndex - 3, 0), filteredIndex);
        })
        .map('text')
        .join(' \n ')
        .value();

      let data = {
        answer: answer,
        question: question,
        pageId: this.website.pageId,
        threadId,
        messageId,
        context: agentContextMessages,
        history: history,
      };

      window.parent.postMessage(data, '*');
    },
    sendSuggestion(suggestion) {
      this.text = suggestion;
      let payload = {
        visitorId: this.website.visitorId,
        name: 'widget_quick_button_clicked',
        type: 'juphy',
        data: {
          question: suggestion,
        },
      };
      this.pushIntoLine();
      this.messages = this.messages.filter((message) => !('actionButton' in message));
      this.sendEngagementLogV2(payload);
    },
    sendQuickAnswer(answer) {
      // Clear potentialAnswers from all messages so buttons disappear
      this.messages.forEach((msg) => {
        if (msg.potentialAnswers) {
          msg.potentialAnswers = [];
        }
      });

      this.messages.push({ text: answer, type: 'agent', object: {}, quickAnswer: true });

      this.$nextTick(() => {
        const c = document.getElementById('wcw_messages_container');
        if (c) c.scrollTo({ top: c.scrollHeight, behavior: 'smooth' });
      });

      this.onTheLine.push({ text: answer, type: 'agent', object: {}, quickAnswer: true });
      if (!this.keepingAsking) {
        this.keepingAsking = true;
        this.startRun(this.activeThreadId, this.activeRepliedId);
      }

      let payload = {
        visitorId: this.website.visitorId,
        name: 'widget_quick_answer_clicked',
        type: 'juphy',
        data: {
          question: answer,
        },
      };
      this.sendEngagementLogV2(payload);
    },
    checkOrderStatus() {
              if (SPECIAL_ORDER_TRACKING_PAGE_IDS.includes(this.website.pageId)) {
        if (!this.orderStatus.number.length) return '';
      } else {
        if (!this.orderStatus.email.length || !this.orderStatus.number.length) return '';
      }

      let eventPayload = {
        visitorId: this.website.visitorId,
        type: 'juphy',
        name: 'widget_order_status_requested',
        data: {
          email: this.orderStatus.email.trim(),
          orderNumber: this.orderStatus.number.trim(),
        },
      };

      this.loading.typing = true;

      // Special handling for specific pageIds
      if (SPECIAL_ORDER_TRACKING_PAGE_IDS.includes(this.website.pageId)) {
        _system.services.chatbot.getOrderStatus(
          {
            query: {
              orderId: this.orderStatus.number.trim(),
            },
          },
          (err, response) => {
            if (err) {
              eventPayload.data.isOrderFound = false;
              this.sendEngagementLogV2(eventPayload);
              console.error('Error:', err);
              this.loading.typing = false;
              return;
            }

            if (!response || response.data.isOrderFound === false) {
              eventPayload.data.isOrderFound = false;
              eventPayload.data.orderUrl = response.data.url;
              this.messages.push({
                text:
                  this.browserLang === 'ar'
                    ? 'لم نتمكن من العثور على أي طلب بالمعلومات المقدمة. يرجى التأكد من إدخال رقم التتبع الصحيح.'
                    : 'We could not find any order with the given information. Please make sure you have entered the correct tracking number.',
                type: 'bot',
              });
            } else {
              eventPayload.data.isOrderFound = true;
              this.messages.push({
                text: `We found your order. Here are the details:`,
                url: response.data.url || '#',
                line_items: response.data.products,
                type: 'bot',
                orderResult: true,
                fulfillment_status: response.data.status,
              });
            }
            this.loading.typing = false;
            this.sendEngagementLogV2(eventPayload);

            setTimeout(() => {
              document.getElementById('wcw_messages_container').scrollTo({
                top: document.getElementById('wcw_messages_container').scrollHeight,
                behavior: 'smooth',
              });
            }, 100);
          }
        );
        return;
      }

      let payload = {
        body: {
          email: this.orderStatus.email.trim(),
          number: this.orderStatus.number.trim(),
          pageId: this.website.pageId,
        },
      };

      _system.services.chatbot.checkOrderStatus(payload, (err, response) => {
        if (err) {
          eventPayload.data.isOrderFound = false;
          this.sendEngagementLogV2(eventPayload);
          console.error('Error:', err);
          this.loading.typing = false;
          return;
        }

        let order_status_url = _.get(response, 'orderByNumber.[0].order_status_url', '');
        let line_items = _.get(response, 'orderByNumber.[0].line_items', '');

        if (!order_status_url.length) {
          eventPayload.data.isOrderFound = false;
          this.messages.push({
            text: 'We could not find any order with the given information. Please make sure you have entered the correct email and order number.',
            type: 'bot',
          });
        } else {
          eventPayload.data.isOrderFound = true;
          eventPayload.data.orderUrl = order_status_url;
          const currency = _.get(response, 'orderByNumber.[0].currency', null);
          this.messages.push({
            text: `We found your order. You can view the details by clicking the button below.`,
            url: order_status_url,
            line_items: line_items,
            type: 'bot',
            orderResult: true,
            fulfillment_status: _.get(response, 'orderByNumber.[0].fulfillment_status', null),
            currency: currency,
            currencySymbol: getCurrencySymbol(currency),
          });
        }
        this.loading.typing = false;
        this.sendEngagementLogV2(eventPayload);

        setTimeout(() => {
          document.getElementById('wcw_messages_container').scrollTo({
            top: document.getElementById('wcw_messages_container').scrollHeight,
            behavior: 'smooth',
          });
        }, 100);
      });
    },
    hexToRgb(hex) {
      const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
      hex = hex.replace(shorthandRegex, (m, r, g, b) => r + r + g + g + b + b);
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result
        ? {
            r: parseInt(result[1], 16),
            g: parseInt(result[2], 16),
            b: parseInt(result[3], 16),
          }
        : { r: 0, g: 0, b: 0 }; // Default to black if conversion fails
    },
    calculateBrightness(color) {
      const { r, g, b } = this.hexToRgb(color);
      return (r * 299 + g * 587 + b * 114) / 1000;
    },
    handleEngagement(question) {
      if (!this.engagement) this.startEngagement();
      let payload = {
        visitorId: this.website.visitorId,
        type: 'juphy',
        name: 'widget_engagement_started',
        data: {
          question: question,
        },
      };
      this.sendEngagementLogV2(payload);
    },
    startEngagement() {
      localStorage.setItem('juphy_widget_engaged', new Date().toISOString());
      this.engagement = new Date().toISOString();
    },
    sendEngagementLogV2(eventData) {
      if (!this.currentUrl || this.juphy) return;
      let currentUrl = new URL(this.currentUrl);
      let fullUrl = currentUrl.href;
      const currentTime = new Date();

      eventData.visitorId = this.website.visitorId;
      eventData.timestamp = currentTime.toISOString();
      eventData.pageId = this.website.pageId;

      eventData.id = eventData.id || this.generateEventId();
      eventData.data.url = fullUrl;

      const payload = {
        body: eventData,
      };

      _system.services.chatbot.sendEngagementDataV2(payload, (err, res) => {
        if (err) {
          console.error(err);
          return;
        }
      });
    },
    trackLinkClick(url) {
      if (url.includes('/products/') || url.includes('/collections/') || url.includes('/pages/')) {
        return;
      }
      let payload = {
        visitorId: this.website.visitorId,
        type: 'juphy',
        name: 'widget_link_clicked',
        data: {
          url: url,
        },
      };
      this.sendEngagementLogV2(payload);
    },
    messageHandlers() {
      return {
        sendInitialMessage: (data) => {
          setTimeout(() => {
            this.messages = [{ text: data, type: 'bot' }];
          }, 100);
        },

        productModule: (eventData) => {
          if (eventData.productData.productJson) {
            this.dropOffProductJson = eventData.productData.productJson;
          }
          this.$refs.inputField.focus();
          this.sendEngagementLogV2(eventData.payload);
        },

        dropOffData: (eventData) => {
          if (eventData.dropOffData.productJson) {
            this.dropOffProductJson = eventData.dropOffData.productJson;
          }
          this.$refs.inputField.focus();
          this.sendEngagementLogV2(eventData.payload);
        },

        smartProductPopup: (eventData) => {
          this.sendEngagementLogV2(eventData.payload);
        },

        predefinedQuestion: (data) => {
          this.text = data.question;
          if (data.context) {
            this.retrainContext = data.context;
          }
          this.pushIntoLine();
        },

        juphyModuleQuickButton: (quickButtonData) => {
          this.text = quickButtonData.payload.data.moduleButtonText;
          if (quickButtonData.productData) {
            this.dropOffProductJson = quickButtonData.productData;
          }
          this.pushIntoLine();
          this.sendEngagementLogV2(quickButtonData.payload);
        },

        focusInput: (eventData) => {
          this.$refs.inputField.focus();
          if (eventData.productData) {
            this.dropOffProductJson = eventData.productData;
          }
          this.sendEngagementLogV2(eventData.payload);
        },

        welcomeModule: (eventData) => {
          this.sendEngagementLogV2(eventData.payload);
        },

        customModule: (eventData) => {
          if (eventData.productJson) {
            this.dropOffProductJson = eventData.productJson;
          }
          this.sendEngagementLogV2(eventData.payload);
        },

        cartModule: (eventData) => {
          this.sendEngagementLogV2(eventData.payload);
        },

        returningCustomerModule: (eventData) => {
          this.sendEngagementLogV2(eventData.payload);
        },

        collectionModule: (eventData) => {
          this.sendEngagementLogV2(eventData.payload);
        },

        relatedData: async (data) => {
          this.$refs.inputField.focus();
          this.messages = this.messages.filter(
            (message) => !(message.actionButton === true && message.type === 'agent')
          );

          const messages = data.relatedProducts.products.map((product) => {
            const productUrl = new URL(`${data.baseUrl}${product.url}`);
            productUrl.searchParams.set('crossSelling', 'true');

            return {
              title: product.title.substring(0, 28),
              desc:
                new DOMParser()
                  .parseFromString(product.description + '...', 'text/html')
                  .body.textContent.substring(0, 28) || '',
              image: product.featured_image,
              url: productUrl.toString(),
              rich: true,
              multiple: true,
              type: 'bot',
            };
          });

          const compactedArray = _.compact(messages);
          if (compactedArray.length) {
            this.messages.push({
              rich: true,
              type: 'bot',
              multiple: true,
              messages: compactedArray,
            });
            this.loading.typing = false;

            setTimeout(() => {
              document.getElementById('wcw_messages_container').scrollTo({
                top: document.getElementById('wcw_messages_container').scrollHeight,
                behavior: 'smooth',
              });
            }, 100);
          } else {
            this.loading.typing = false;
          }
        },
        updateConfig: (data) => {
          this.botConfig = data.config;

          this.messages = [
            {
              text: this.botConfig.welcome,
              type: 'bot',
            },
          ];
          this.messages.push({ actionButton: true, type: 'agent' });
          this.setWidgetLanguage(this.botConfig.language);
        },
        updateWidgetStyle: (data) => {
          this.botConfig.style = data.style;
        },
        showConversation: (data) => {
          if (this.onRetrain === 'true') {
            this.messages = data.messages;
          }
        },
        addedToCart: (data) => {
          this.sendingToCartSet.delete(data.variantId);
          this.addedToCartSet.add(data.variantId);
          setTimeout(() => this.addedToCartSet.delete(data.variantId), 2000);
        },
        showReportMessage: (data) => {
          let conversation = data;
          let newMessages = conversation.messages.map((message) => {
            return {
              text: message.text,
              type: message.from.name === 'assistant' ? 'bot' : 'agent',
              createdAt: message.createdAt,
            };
          });

          /*               newMessages.unshift({
                  text: this.botConfig.welcome,
                  type: 'bot'
              }); */

          this.messages = newMessages;
        },
      };
    },

    // Main message handler
    handleMessage(event) {
      const { action, data } = event.data;

      // Get the handler for this action
      const handler = this.messageHandlers()[action];
      if (handler && data) {
        handler(data);
      }
    },
    normalizeActionButtons(config) {
      if (_.isArray(config.actionButtons) && config.actionButtons.length > 0) {
        // If the first item is a string, assume all are strings
        if (_.isString(config.actionButtons[0])) {
          config.actionButtons = config.actionButtons.map((text) => ({
            text,
            lang: 'primary',
          }));
        }
      }
    },

    animateAndSend() {
      this.pushIntoLine();

      const button = document.getElementById('wcw_form_button');
      button.classList.add('animate');

      setTimeout(() => {
        button.classList.remove('animate');
      }, 600);
    },
    isValidDiscount(price, compareAtPrice) {
      const priceNum = parseFloat(price);
      const compareNum = parseFloat(compareAtPrice);
      if (!compareNum || compareNum === 0) return false;
      const percent = (1 - priceNum / compareNum) * 100;
      return isFinite(percent) && !isNaN(percent) && percent > 0;
    },
    calculateDiscount(price, compareAtPrice) {
      const priceNum = parseFloat(price);
      const compareNum = parseFloat(compareAtPrice);
      if (!compareNum || compareNum === 0) return '';
      const percent = (1 - priceNum / compareNum) * 100;
      return isFinite(percent) && !isNaN(percent) ? Math.round(percent) : '';
    },
    fetchTools() {
      if (!this.website.pageId) return;
      _system.services.tools.list({ query: { pageId: this.website.pageId } }, (err, res) => {
        if (err) return;
        const tools = (res && res.tools) || [];
        const registry = {};
        const clientTools = [];
        
        tools.forEach((tool) => { 
          if (tool && tool.name) {
            registry[tool.name] = tool;
            
            // Collect client tools to send to parent
            if (tool.type === 'client' && tool.typeData && tool.typeData.client) {
              clientTools.push({
                name: tool.name,
                script: tool.typeData.client.clientScript
              });
            }
          }
        });
        
        this.toolsRegistry = registry;
        
        // Send client tools to parent window to register them
        if (clientTools.length > 0) {
          window.parent.postMessage({
            action: 'registerClientTools',
            tools: clientTools
          }, '*');
        }
      });
    },
    renderToolMessage(toolCall) {
      const { name, arguments: rawArguments } = toolCall.function || {};
      // Resolve tool directly by name (aiTool.name === tool.name)
      const resolvedTool = this.toolsRegistry[name];
      if (!resolvedTool) {
        return;
      }
      
      const parsedArguments = (() => { try { return rawArguments ? JSON.parse(rawArguments) : {}; } catch { return {}; } })();
      
      // Handle client tools - execute directly via postMessage to parent
      if (resolvedTool.type === 'client') {
        window.parent.postMessage({
          action: 'executeClientTool',
          toolName: name,
          args: parsedArguments
        }, '*');
        
        // Optionally show confirmation in chat
        const instructionText = parsedArguments && parsedArguments.juphyInstructionText;
        if (instructionText && typeof instructionText === 'string' && instructionText.trim().length > 0) {
          this.messages.push({ text: instructionText, type: 'bot' });
        }
        
        this.$nextTick(() => {
          const container = document.getElementById('wcw_messages_container');
          if (container) container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
        });
        return;
      }
      
      // Handle other tool types (button, input, forms, imageUpload)
      // Pass instructionText to the tool component instead of rendering separately
      const instructionText = parsedArguments && parsedArguments.juphyInstructionText;
      this.messages.push({ 
        type: 'tool', 
        tool: resolvedTool, 
        args: parsedArguments,
        instructionText: (instructionText && typeof instructionText === 'string' && instructionText.trim().length > 0) ? instructionText : null
      });
      this.$nextTick(() => {
        const container = document.getElementById('wcw_messages_container');
        if (container) container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
      });
    },
    async handleToolExecution(executionEvent, onComplete) {
      try {
        const { kind, tool, data = {}, file, messageIndex } = executionEvent;
        if (!tool) return onComplete && onComplete();

        // Handle multipleChoice selection
        if (kind === 'multipleChoice' && data && typeof data.choiceText === 'string') {
          const choiceText = data.choiceText.trim();
          if (choiceText.length) {
            // Replace tool message with instruction text (keep the AI's message visible)
            if (typeof messageIndex === 'number' && this.messages[messageIndex]?.instructionText) {
              this.messages.splice(messageIndex, 1, { 
                text: this.messages[messageIndex].instructionText, 
                type: 'bot' 
              });
            } else if (typeof messageIndex === 'number') {
              // If no instructionText, remove the tool message
              this.messages.splice(messageIndex, 1);
            }

            // Render the user's choice as if typed
            this.messages.push({ text: choiceText, type: 'agent', object: {} });

            // Scroll down after render
            this.$nextTick(() => {
              const container = document.getElementById('wcw_messages_container');
              if (container) container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
            });

            // Queue the message to backend and start run
            this.onTheLine.push({ text: choiceText, type: 'agent', object: {} });
            if (!this.keepingAsking) {
              this.keepingAsking = true;
              this.startRun(this.activeThreadId, this.activeRepliedId);
            }
          }
          onComplete && onComplete();
          return;
        }

        // Handle queued image attachment selection (no immediate upload)
        if (kind === 'queueImageAttachment' && file) {
          if (!Array.isArray(this.pendingFiles)) this.pendingFiles = [];
          this.pendingFiles.push({ file, name: file.name, size: file.size });
          this.$nextTick(() => {
            const c = document.getElementById('wcw_messages_container');
            if (c) c.scrollTo({ top: c.scrollHeight, behavior: 'smooth' });
          });
          onComplete && onComplete();
          return;
        }

        // Upload image immediately but queue it (WhatsApp-style)
        if (kind === 'imageUpload' && file) {
          // Create FormData for file upload
          const formData = new FormData();
          formData.append('file', file);

          // Upload to Cloudflare endpoint using service
          _system.services.cloudflare.uploadImage({ formData }, (error, response) => {
            if (error) {
              console.error('Image upload error:', error);
              this.messages.push({ text: 'Failed to upload image. Please try again.', type: 'bot' });
              onComplete && onComplete();
              this.$nextTick(() => {
                const container = document.getElementById('wcw_messages_container');
                if (container) container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
              });
              return;
            }

            const permanentUrl = response?.permanentUrl;
            if (!permanentUrl) {
              console.error('No permanentUrl in response:', response);
              this.messages.push({ text: 'Failed to get permanent image URL.', type: 'bot' });
              onComplete && onComplete();
              this.$nextTick(() => {
                const container = document.getElementById('wcw_messages_container');
                if (container) container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
              });
              return;
            }

            // Preload the image before showing preview
            const img = new Image();
            img.onload = () => {
              // Add to pending uploaded images after image is loaded
              if (!Array.isArray(this.pendingUploadedImages)) this.pendingUploadedImages = [];
              this.pendingUploadedImages.push({ 
                url: permanentUrl, 
                name: file.name, 
                size: file.size 
              });

              onComplete && onComplete();

              this.$nextTick(() => {
                const container = document.getElementById('wcw_messages_container');
                if (container) {
                  container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
                }
              });
            };
            img.onerror = () => {
              // If image fails to load, still add it (it might load later)
              if (!Array.isArray(this.pendingUploadedImages)) this.pendingUploadedImages = [];
              this.pendingUploadedImages.push({ 
                url: permanentUrl, 
                name: file.name, 
                size: file.size 
              });
              onComplete && onComplete();
            };
            img.src = permanentUrl;
          });
          return;
        }

        // Redirect paths
        // Unified redirect handling for button/input/forms
        const toolType = tool.type;
        const typeData = (tool?.typeData && tool.typeData[toolType]) ? tool.typeData[toolType] : {};
        const redirectUrl = typeData && typeData.redirectUrl;
        if (redirectUrl) {
          const urlParams = { ...data, ...(executionEvent.args || {}) };
          const url = templateUrl(redirectUrl, urlParams);
          const openInNewTab = toolType === 'button' ? Boolean(typeData.openInNewTab) : true;
          openUrl(url, openInNewTab);
          if (toolType === 'button' && typeData.confirmationText) {
            this.messages.push({ text: typeData.confirmationText, type: 'bot' });
          }
          return onComplete && onComplete();
        }
        // Manual execute
        const conversationHistoryData = this.messages.map((msg) => {
          if (msg.type === 'tool' && msg.tool) {
            const toolName = msg.tool.name || msg.tool.type;
            const instructionText = msg.instructionText || '';
            return { 
              type: 'bot', 
              text: instructionText || 'Tool was presented to user.',
              toolCalled: toolName
            };
          }
          return { type: msg.type, text: msg.text };
        }).slice(-12);
        const lastAgentText = [...this.messages].reverse().find((msg) => msg.type === 'agent')?.text || '';
        this.loading.typing = true;
        _system.services.tools.executeManual({
          query: { pageId: this.website.pageId },
          body: { name: tool.name, params: data, content: lastAgentText, conversationHistory: conversationHistoryData }
        }, (error, response) => {
          if (error) {
            this.loading.typing = false;
            this.messages.push({ text: 'There was an error while executing your request.', type: 'bot' });
            onComplete && onComplete();
            this.$nextTick(() => {
              const container = document.getElementById('wcw_messages_container');
              if (container) container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
            });
            return;
          }
          if (response && response.toolResult && response.toolResult.success && response.answer) {
            this.messages.push({ text: response.answer, type: 'bot' });
          } else if (response && response.toolResult && response.toolResult.success === false) {
            const failureText = response.toolResult.error || 'Sorry, I could not complete that.';
            this.messages.push({ text: failureText, type: 'bot' });
          }
          this.loading.typing = false;
          onComplete && onComplete();
          this.$nextTick(() => {
            const container = document.getElementById('wcw_messages_container');
            if (container) container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
          });
        });
      } catch (e) {
        console.error('Tool execution error:', e);
        this.messages.push({ text: 'There was an error while executing your request.', type: 'bot' });
        onComplete && onComplete();
      }
    },
    getCurrentBaseUrl() {
      const url = new URL(this.currentUrl);
      return url.origin;
    },
    openWhatsApp(contact) {
      if (contact) {
        const phoneNumber = contact.replace(/\D/g, '');
        const whatsappUrl = `https://wa.me/${phoneNumber}`;
        window.open(whatsappUrl, '_blank');
      }
    },
    openPhone(contact) {
      if (contact) {
        const phoneNumber = contact.replace(/\D/g, '');
        window.location.href = `tel:${phoneNumber}`;
      }
    },
    openEmail(contact) {
      if (contact) {
        window.location.href = `mailto:${contact}`;
      }
    },
  },
  mounted() {
    window.parent.postMessage({ action: 'requestConfig' }, '*');
    this.$nextTick(() => this.fetchTools());
  },
  updated() {
    this.initCarouselArrows();
  },
  created() {
    window.addEventListener('message', (event) => {
      if (event.data && event.data.action === 'sendConfig') {
        this.botConfig = event.data.juphy_widget_config;
        this.headerVisibility = event.data.juphy_widget_config?.headerVisibility ?? true;
        this.normalizeActionButtons(this.botConfig);
        
        // Store parent window width from juphy.js
        if (event.data.parentWindowWidth !== undefined) {
          this.parentWindowWidth = event.data.parentWindowWidth;
        }
        
        // Only set mobile fullscreen state on mobile devices
        if (this.isMobile && this.botConfig.widget_fullscreen !== undefined) {
          this.isMobileFullscreen = this.botConfig.widget_fullscreen;
        }
        
        this.initializeWidget();
        this.setWidgetLanguage(this.botConfig.language);
        // Fetch tools once we have pageId
        this.$nextTick(() => this.fetchTools());
      }
    });
    this.engagement = localStorage.getItem('juphy_widget_engaged') || null;
    const engagementTime = new Date(this.engagement);
    const oneDayInMs = 24 * 60 * 60 * 1000;
    const isOlderThanOneDay = new Date() - engagementTime > oneDayInMs;
    this.engagement = isOlderThanOneDay ? null : this.engagement;
  },
  beforeDestroy() {
    window.removeEventListener('message', this.handleMessage);
  },
};
</script>

<template>
  <div
    v-if="botConfig.name"
    data-aos="fade-left"
    id="WebChatwidget"
    style="width: 100%"
    class="WebChatwidget"
    :class="{ 'mobile-75-height': isMobile && !isMobileFullscreen }"
    :data-page-id="website.pageId"
    :data-extension="extension"
  >
    <div class="wcw_wrapper">
      <div v-if="!onRetrain && (!extension || botConfig?.extension?.hasHeader)" class="wcw_header">
        <div 
          v-if="!juphy && isMobile"
          class="mobile-drag-indicator" 
          :style="{ background: botConfig.style.header.backgroundColor }"
          @click="toggleMobileHeight"
        >
          <div class="drag-line"></div>
        </div>
        <div :style="{ background: botConfig.style.header.backgroundColor }" class="content" :class="{ 'no-drag-toggle': juphy }" @click="!juphy && isMobile && toggleMobileHeight()">
          <div v-if="botConfig?.identity?.avatar" class="agents">
            <img :src="botConfig?.identity?.avatar" alt="" />
          </div>
          <div class="status" :class="{ 'hide-status': website?.pageId === '67a84f9693d3c43ed3e0d6a0' && currentUrl.includes('/glowchat') }">
            <div class="label" style="opacity: 0.9">
              <div :style="{ color: botConfig.style.header.name.textColor }" class="widgetName">
                {{ extension && botConfig.extension?.text ? botConfig.extension.text : botConfig.name }}
                <span v-if="local && testing">[test]</span>
              </div>
            </div>
            <div class="label" v-if="!extension">
              <div
                :style="{ color: botConfig.style.header.status.textColor, display: 'flex', alignItems: 'center' }"
                class="widgetStatus"
              >
                <span class="indicator"></span>
                {{ contentLang.alwaysActive }}
              </div>
            </div>
          </div>
          <div
            @click.stop="close()"
            id="close-iframe"
            class="wcw_close"
            :class="{ 'custom-number-c-close': website?.pageId === '67a84f9693d3c43ed3e0d6a0' }"
            v-if="!report && headerVisibility"
          >
            <svg
              :style="{ color: botConfig.style.header.name.textColor }"
              xmlns="http://www.w3.org/2000/svg"
              fill="#ffffff"
              viewBox="0 0 24 24"
              stroke-width="1.5"
              stroke="currentColor"
              id="close-iframe"
            >
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </div>
          <!--               <div id="wcw_close" class="wcw_close" onclick="triggerWidget()">
                <svg
                  class="svg"
                  width="24px"
                  height="24px"
                  enablebackground="new 0 0 32 32"
                  fill="#72777A"
                  stroke="none"
                  version="1.1"
                  viewBox="-3.2 -3.2 32.4 38.4"
                  xmlspace="preserve"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="m16 13c-1.654 0-3 1.346-3 3s1.346 3 3 3 3-1.346 3-3-1.346-3-3-3z"
                  ></path>
                  <path
                    d="m6 13c-1.654 0-3 1.346-3 3s1.346 3 3 3 3-1.346 3-3-1.346-3-3-3z"
                  ></path>
                  <path
                    d="m26 13c-1.654 0-3 1.346-3 3s1.346 3 3 3 3-1.346 3-3-1.346-3-3-3z"
                  ></path>
                </svg>
              </div> -->
        </div>
      </div>
      <div
        id="wcw_messages_container"
        :style="{ background: botConfig.style.middle.backgroundColor }"
        class="wcw_messages_container"
      >
        <ul id="wcw_messages">
          <template v-for="(message, index) in messages" :key="message.text || message.tool?.name || index">
          <li :class="'clearfix ' + (message.type === 'tool' ? 'bot' : message.type)">
            <div
              v-if="botConfig?.popupModules?.style && message.actionButton && messages.length <= 2"
              style="max-width: 90%; padding-right: 0; background-color: transparent"
              :class="'content ' + message.type"
            >
              <button
                v-for="(actionButton, buttonIndex) in filteredActionButtons"
                :key="buttonIndex"
                class="juphy-button"
                :class="'content ' + message.type"
                @click="sendSuggestion(actionButton.text)"
                :style="{
                  background: botConfig?.popupModules?.style?.button.backgroundColor || '#fff',
                  border: `1.5px solid ${botConfig?.popupModules?.style?.button.borderColor || '#ccc'}`,
                  color: botConfig?.popupModules?.style?.button.textColor || '#000',
                  float: 'right',
                }"
              >
                {{ actionButton.text }}
              </button>
            </div>
            <!-- <img
                  v-if=" message.type == 'bot'"
                  class="msgIcon"
                  src="https://www.svgrepo.com/show/382095/female-avatar-girl-face-woman-user-4.svg"
                  alt=""
                /> -->

            <div
              v-if="!message.actionButton && message.type !== 'log'"
              :style="{
                background: ((message.image && !message.hasImages) || message.rich)
                  ? 'transparent'
                  : botConfig.style.middle[message.type === 'tool' ? 'bot' : message.type].backgroundColor,
                color: botConfig.style.middle[message.type === 'tool' ? 'bot' : message.type].textColor,
                padding: (message.image && !message.hasImages) ? '0' : '10px 1rem',
                borderRadius: null,
              }"
              :class="'content ' + (message.type === 'tool' ? 'bot' : message.type)"
            >
              <!-- Combined message with images (WhatsApp style) -->
              <div v-if="message.hasImages && message.images && message.images.length">
                <div style="display: flex; flex-wrap: wrap; gap: 4px; margin-bottom: 8px;">
                  <img
                    v-for="(imgUrl, idx) in message.images"
                    :key="idx"
                    :src="imgUrl"
                    alt="Uploaded image"
                    style="max-width: 100%; border-radius: 8px; display: block;"
                  />
                </div>
                <span v-html="message.text.split('\n').filter(line => !line.startsWith('img:')).join('\n')" v-track-links></span>
              </div>
              
              <!-- Single image message (no text) -->
              <img
                v-else-if="message.image && message.imageUrl"
                :src="message.imageUrl"
                alt="Uploaded image"
                style="max-width: 100%; border-radius: 8px; display: block;"
              />
              
              <!-- Text-only message -->
              <span
                v-else-if="!message.rich && !message.actionButton && !message.order && !message.orderResult && message.type !== 'tool' && !message.image && !message.hasImages"
                v-html="message.text"
                v-track-links
              ></span>
              <!-- For multipleChoice: show instruction text in bubble, buttons outside -->
              <span
                v-if="message.type === 'tool' && message.tool && message.tool.type === 'multipleChoice' && message.instructionText"
                v-html="message.instructionText"
                v-track-links
              ></span>
              <!-- For other tools: render normally -->
              <ToolRenderer
                v-if="message.type === 'tool' && message.tool && message.tool.type !== 'multipleChoice'"
                :tool="message.tool"
                :args="message.args"
                :styles="botConfig.style"
                :messageIndex="index"
                :instructionText="message.instructionText"
                @execute="handleToolExecution"
              />

              <div
                v-if="message.rich && message.multiple"
                class="richMsg-wrapper"
              >
                <button
                  class="carousel-arrow carousel-arrow-left hidden"
                  :style="{ color: botConfig.style.footer.button.backgroundColor }"
                  @click="scrollCarousel($event, -1)"
                  aria-label="Previous products"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>
                </button>
                <div
                  class="richMsg"
                  :style="{
                    backgroundColor: botConfig.style.middle.backgroundColor,
                    maxWidth: juphy ? '365px' : botConfig.widget?.chatOpeningStyle === 'drawer' ? '97vw' : '382px',
                  }"
                  @scroll="updateCarouselArrows($event)"
                >
                  <div v-for="message in message.messages" :key="message.text" class="richMsgContainer">
                    <div
                      v-if="message.image.length"
                      class="richMsgImg"
                      :style="
                        'background: url(' +
                        message.image +
                        '); background-size: cover; background-repeat: no-repeat; background-position: center; background-color: #fff;'
                      "
                    ></div>
                    <div class="richMsgFooter">
                      <div class="richMsgHeader">
                        <div class="richMsgTitle">
                          {{ message.title.length > 20 ? message.title.substring(0, 20) + '...' : message.title }}
                        </div>
                        <div v-if="message.price" class="richMsgPrice" :class="{ 'has-svg-icon': getCurrencyIcon && getCurrencyIcon(message.currency) }">
                          <span v-if="getCurrencyIcon && getCurrencyIcon(message.currency)" v-html="getCurrencyIcon(message.currency)"></span>
                          <span v-else>{{ message.currencySymbol }}</span>
                          <span>{{ message.price }}</span>
                          <span v-if="message.compareAtPrice && isValidDiscount(message.price, message.compareAtPrice)" class="originalPrice">
                            <span v-if="getCurrencyIcon && getCurrencyIcon(message.currency)" v-html="getCurrencyIcon(message.currency)"></span>
                            <span v-else>{{ message.currencySymbol }}</span>
                            {{ message.compareAtPrice }}
                          </span>
                          <span
                            v-if="message.compareAtPrice && isValidDiscount(message.price, message.compareAtPrice)"
                            class="discountBadge"
                          >
                            -{{ calculateDiscount(message.price, message.compareAtPrice) }}%
                          </span>
                        </div>
                      </div>
                      <div class="richMsgButton">
                        <a
                          :style="{
                            color: botConfig.style.footer.button.textColor,
                            backgroundColor: botConfig.style.footer.button.backgroundColor,
                            borderRadius: botConfig.isAddToCartEnabled ? '24px 0 0 24px' : '2px',
                            textAlign: 'center',
                            boxShadow: botConfig.isAddToCartEnabled ? '1px 0 0 0 rgba(255, 255, 255, 0.15)' : 'none',
                            border: 'none',
                            flex: '1 1 0%',
                            padding: botConfig.isAddToCartEnabled ? '12px 2px' : '12px 16px',
                            textDecoration: 'none',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }"
                          target="_blank"
                          :href="message.url"
                          class="button2"
                        >
                          {{ message.isCollection ? contentLang.collectionDetailsLink : contentLang.productDetailsLink }}
                        </a>
                        <button
                          v-if="botConfig.isAddToCartEnabled"
                          :style="{
                            color: botConfig.style.footer.button.textColor,
                            backgroundColor: botConfig.style.footer.button.backgroundColor,
                            borderRadius: '0 24px 24px 0',
                            textAlign: 'center',
                            boxShadow: '-1px 0 0 0 rgba(0, 0, 0, 0.15)',
                            border: 'none',
                            flex: '1 1 0%',
                            padding: '12px 2px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }"
                          @click="addToCart(message)"
                          :disabled="isButtonLoading(message.variantId) || isCartButtonAdded(message.variantId)"
                        >
                          <span v-if="isButtonLoading(message.variantId)">
                            <span class="spinner"></span>
                          </span>
                          <span v-else-if="isCartButtonAdded(message.variantId)">
                            <span class="added-checkmark">&#10003;</span>
                            <span class="added-text">Added!</span>
                          </span>
                          <span v-else>
                            {{ contentLang.addToCartText || 'Add' }}
                          </span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                <button
                  class="carousel-arrow carousel-arrow-right"
                  :style="{ color: botConfig.style.footer.button.backgroundColor }"
                  @click="scrollCarousel($event, 1)"
                  aria-label="Next products"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>
                </button>
              </div>
              <div v-if="message.order" class="richMsg order">
                <div style="display: flex; flex-direction: column; width: 100%; margin: 0 auto">
                  <span
                    v-if="!specialOrderTrackingPageIds.includes(website.pageId)"
                    style="margin-bottom: 0.5rem"
                    v-html="message.text"
                  ></span>
                  <span v-else style="margin-bottom: 0.5rem">{{
                    browserLang === 'ar'
                      ? 'للتحقق من حالة الطلب، يرجى إدخال رقم التتبع الخاص بك.'
                      : 'To check the status of your order, please insert your tracking number.'
                  }}</span>
                  <input
                    v-if="!specialOrderTrackingPageIds.includes(website.pageId)"
                    v-model="orderStatus.email"
                    type="text"
                    placeholder="Email"
                    :style="{
                      background: botConfig.style.footer.input.backgroundColor,
                      color: botConfig.style.footer.input.textColor,
                      'border-color': botConfig.style.footer.input.borderColor,
                    }"
                  />
                  <input
                    v-model="orderStatus.number"
                    type="text"
                    :placeholder="contentLang.orderTrackingPlaceHolder"
                    :style="{
                      background: botConfig.style.footer.input.backgroundColor,
                      color: botConfig.style.footer.input.textColor,
                      'border-color': botConfig.style.footer.input.borderColor,
                    }"
                  />
                  <button
                    :disabled="loading.typing"
                    @click="checkOrderStatus()"
                    type="button"
                    :style="{
                      color: botConfig.style.footer.button.textColor,
                      backgroundColor: botConfig.style.footer.button.backgroundColor,
                    }"
                  >
                    {{ contentLang.submitButton }}
                  </button>
                </div>
              </div>

              <div v-if="message.orderResult" class="richMsg order">
                <div
                  style="
                    display: flex;
                    flex-direction: column;
                    width: 100%;
                    margin: 0 auto;
                    background: #fff;
                    border-radius: 12px;
                    padding: 20px;
                  "
                >
                  <div v-if="message.fulfillment_status" style="margin-bottom: 20px">
                    <span
                      style="
                        padding: 8px 16px;
                        border-radius: 6px;
                        font-size: 14px;
                        background-color: #f5f5f5;
                        display: inline-block;
                        font-weight: 500;
                      "
                      :style="{
                        color: botConfig.style.middle['bot'].textColor,
                      }"
                    >
                      Status:
                      {{ message.fulfillment_status.charAt(0).toUpperCase() + message.fulfillment_status.slice(1) }}
                    </span>
                  </div>
                  <ul v-if="message.line_items.length" style="padding: 0; margin: 0 0 16px 0; width: 100%">
                    <li
                      v-for="line_item in message.line_items"
                      :key="line_item.name"
                      style="list-style: none; margin-bottom: 12px"
                    >
                      <div
                        style="
                          display: flex;
                          justify-content: space-between;
                          align-items: center;
                          font-size: 15px;
                          line-height: 1.4;
                        "
                      >
                        <span style="color: #2c3338; flex: 1; padding-right: 16px">{{ line_item.name }}</span>
                        <span v-if="line_item.price && message.currencySymbol" style="color: #2c3338; font-weight: 500; display: flex; align-items: center; gap: 4px;">
                          <span v-if="getCurrencyIcon && getCurrencyIcon(message.currency)" v-html="getCurrencyIcon(message.currency)" style="width: 0.85em; height: 0.85em;"></span>
                          <span v-else>{{ message.currencySymbol }}</span>
                          <span>{{ line_item.price }}</span>
                        </span>
                      </div>
                    </li>
                  </ul>
                  <div v-if="message.url && message.url !== '#'" class="richMsgButton">
                    <a
                      :style="{
                        color: botConfig.style.footer.button.textColor,
                        backgroundColor: botConfig.style.footer.button.backgroundColor,
                        textAlign: 'center',
                        border: 'none',
                        width: '100%',
                        borderRadius: '24px',
                        padding: '12px',
                        textDecoration: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: '500',
                        fontSize: '15px',
                        transition: 'opacity 0.2s ease',
                      }"
                      :class="{ 'button-hover': true }"
                      target="_blank"
                      :href="message.url"
                      type="button"
                    >
                      View Order Details
                    </a>
                  </div>
                </div>
              </div>
            </div>
            <!-- Render multipleChoice buttons outside the bubble -->
            <div
              v-if="message.type === 'tool' && message.tool && message.tool.type === 'multipleChoice'"
              class="mc-buttons-container"
            >
              <ToolRenderer
                :tool="message.tool"
                :args="message.args"
                :styles="botConfig.style"
                :messageIndex="index"
                :instructionText="null"
                @execute="handleToolExecution"
              />
            </div>

            <p
              v-if="
                message.type == 'bot' && juphy && index !== 0 && !message.rich && !message.order && !message.orderResult
              "
              class="retrainButton"
              :style="{ color: botConfig.style.middle['bot'].textColor }"
              @click="retrainMessage(message.text, messages[index - 1].text, index)"
            >
              Retrain
            </p>
          </li>

          <!-- Disambiguation quick answer buttons - separate li so they align to the right -->
          <li
            v-if="message.potentialAnswers && message.potentialAnswers.length && index === messages.length - 1"
            class="clearfix quick-answers-li"
          >
            <div
              class="quick-answers-container"
              :style="{ '--qa-color': botConfig.style.middle.agent.backgroundColor }"
            >
              <button
                v-for="(answer, answerIdx) in message.potentialAnswers"
                :key="answerIdx"
                class="quick-answer-btn"
                @click="sendQuickAnswer(answer)"
              >
                {{ answer }}
              </button>
            </div>
          </li>
          </template>

          <!--               <li :class="'clearfix bot'" >
                <img
                  class="msgIcon"
                  src="https://www.svgrepo.com/show/382095/female-avatar-girl-face-woman-user-4.svg"
                  alt=""
                />
                <div :class="'content bot'">
                  <div class="richMsg">
                    <div class="richMsgContainer">
                      <div class="richMsgTitle">Here is the Puma sneakers 👟 </div>
                      <span class="richMsgDesc">The perfect blend of style and comfort for your everyday adventures.</span>
                      <div class="richMsgImg" style="
                      background: url('https://gisou.com/cdn/shop/products/PDP_Gisou_Hair_Perfume_100ml-01-4x5_600x750_crop_center@2x.jpg?v=1645455449');
                      height: 120px;
                      width: 100%;
                      background-size: contain;
                      background-repeat: no-repeat;
                      background-position: center;
                      background-color: #fff;
                      border-radius: 10px;
                      ">
                      </div>
                        <div class="richMsgButton">
                          <button class="button2">View Product Details</button>
                        </div>
                      </div>
                    </div>
                </div>
              </li> -->

          <li v-if="loading.typing" id="loading___" :class="'clearfix bot typing-indicator'" data-aos="fade-right">
            <!--                 <img
                  class="msgIcon"
                  src="https://www.svgrepo.com/show/382095/female-avatar-girl-face-woman-user-4.svg"
                  alt=""
                /> -->
            <div
              :style="{
                background: botConfig.style.middle['bot'].backgroundColor,
                color: botConfig.style.middle['bot'].textColor,
              }"
              :class="'content bot'"
            >
              <div class="dots">
                <span>{{ contentLang.typing }}</span>
                <span class="dot">.</span>
                <span class="dot">.</span>
                <span class="dot">.</span>
                <!-- This dot will animate last -->
              </div>
            </div>
          </li>
          <!--               <li class="clearfix">
                <div class="content button" style="background: transparent">
                  <div
                    class="button"
                    onclick="buttonContent('Kargo durumunu nasil sorgularim?')"
                    data-content="Kargo durumunu nasil sorgularim?"
                  >
                    Kargo Durumu
                  </div>
                  <div
                    class="button"
                    onclick="buttonContent('Urun iade kosullari nelerdir?')"
                    data-content="Urun iade kosullari nelerdir?"
                  >
                    Urun Iade Kosullari
                  </div>
                </div>
              </li> -->
          <!--               <li
                id="typing"
                class="clearfix"
                style="display: none"
                data-aos="fade-right"
              >
                <div class="content">
                  <img
                    src="https://media.tenor.com/G1VOo6fVsn4AAAAC/keyboard-type.gif"
                    alt=""
                    style="
                      width: 36px;
                      height: 30px;
                      border-radius: 22px;
                      vertical-align: middle;
                      padding-right: 6px;
                    "
                  /><span>typing...</span>
                </div>
              </li> -->
        </ul>
      </div>
      <!--           <div :style="{ background: botConfig.style.middle.backgroundColor }" style="overflow-x: auto; overflow-y: hidden; white-space: nowrap; width: 100%; height: 80px;" v-if="botConfig.actionButtons && botConfig.actionButtons.length > 0">
            <button v-for="suggestion in botConfig.actionButtons" :style="{ background: botConfig.style.middle['bot'].backgroundColor, color: botConfig.style.middle['bot'].textColor }" :key="suggestion"  @click="sendSuggestion(suggestion)" class="quickActionButton"> <span> {{ suggestion }} </span>  </button>
          </div> -->
      <div
        v-if="website?.pageId === '67af9f6390d5c06bdb2a620e' && messages[1]?.actionButton"
        :style="{ background: botConfig.style.middle.backgroundColor }"
      >
        <div
          v-html="disclaimerText"
          :style="{
            backgroundColor: botConfig.style.middle.backgroundColor,
            padding: '1rem',
            fontSize: '12px',
            color: poweredByJuphyColor.color,
            opacity: 0.6,
            fontFamily: 'Arial, Helvetica, sans-serif',
          }"
        ></div>
      </div>
      <!-- Subtle elegant preview above input -->
      <div 
        v-if="(pendingFiles && pendingFiles.length) || (pendingUploadedImages && pendingUploadedImages.length)" 
        style="
          padding: 0 1rem 0 1rem;
          display: flex;
          gap: 6px;
          align-items: center;
          flex-wrap: wrap;
        "
        class="pending-attachments-preview"
      >
        <!-- Uploaded images -->
        <div 
          v-for="img in pendingUploadedImages" 
          :key="img.url" 
          style="
            position: relative;
            border-radius: 6px;
            overflow: visible;
          "
        >
          <img 
            :src="img.url" 
            alt="Preview" 
            style="
              width: 56px;
              height: 56px;
              object-fit: cover;
              display: block;
              border-radius: 6px;
              border: 2px solid rgba(255, 255, 255, 0.9);
              box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
              opacity: 0.92;
            " 
          />
          <button 
            @click="pendingUploadedImages = pendingUploadedImages.filter(i => i.url !== img.url)"
            style="
              position: absolute;
              top: -5px;
              right: -5px;
              background: rgba(255, 255, 255, 0.95);
              color: #8b8b8b;
              border: none;
              border-radius: 50%;
              width: 18px;
              height: 18px;
              cursor: pointer;
              display: flex;
              align-items: center;
              justify-content: center;
              font-size: 13px;
              line-height: 1;
              padding: 0;
              font-weight: 500;
              transition: all 0.2s ease;
              box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
            "
            @mouseover="$event.target.style.color = '#444'; $event.target.style.background = 'rgba(255, 255, 255, 1)'"
            @mouseout="$event.target.style.color = '#8b8b8b'; $event.target.style.background = 'rgba(255, 255, 255, 0.95)'"
          >×</button>
        </div>
        <!-- File attachments -->
        <div 
          v-for="f in pendingFiles" 
          :key="f.name + f.size" 
          style="
            display: flex;
            align-items: center;
            gap: 6px;
            background: rgba(255, 255, 255, 0.9);
            border-radius: 6px;
            padding: 6px 10px;
            box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
            opacity: 0.92;
          "
        >
          <span style="font-size: 16px; opacity: 0.7;">📎</span>
          <span style="font-size: 0.8rem; color: #666; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 150px;">{{ f.name }}</span>
        </div>
      </div>
      <form
        v-if="!onRetrain"
        id="wcw_form"
        :style="{
          background: `linear-gradient( transparent 12%, ${botConfig.style.middle.backgroundColor} 78%)`,
          position: 'relative',
        }"
        :class="{
          'no-branding-margin': !botConfig.hasJuphyBranding || this.extension,
          'contact-info-active': botConfig.contactInformation?.active,
        }"
        @submit.prevent="pushIntoLine()"
      >
        <input
          ref="inputField"
          :style="{
            color: botConfig.style.footer.input.textColor,
            'border-color': botConfig.style.footer.input.borderColor,
          }"
          style="
            background: rgba(255, 255, 255, 0.6);
            z-index: 2;
            border-radius: 30px;
            padding: 10px 62px 10px 24px;
            font-size: 1rem;
          "
          id="wcw_input"
          v-model="text"
          :placeholder="contentLang.textPlaceholder"
          autocomplete="off"
          :disabled="report"
          @click="handleInputClick"
        />
        <button
          id="wcw_form_button"
          :style="{
            background: botConfig.style.footer.button.backgroundColor,
            color: botConfig.style.footer.button.textColor,
          }"
          style="
            z-index: 3;
            border-radius: 50%;
            border: none;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 0;
            line-height: 0;
            cursor: pointer;
          "
          @click="animateAndSend"
        >
          <img
            :src="require('@/assets/sendIcon.svg')"
            width="20"
            height="20"
            style="display: block; transform: translateX(2px)"
          />
        </button>
      </form>

      <!-- Widget Footer -->
      <div
        v-if="!onRetrain"
        class="widget-footer"
        :style="{ background: botConfig.style.middle.backgroundColor, color: botConfig.style.header.status.textColor }"
      >
        <!-- Contact Information Icons -->
        <div
          v-if="botConfig.contactInformation?.active"
          class="contact-icons-container"
        >
          <!-- LiveChat Icon -->
          <button
            v-if="botConfig.contactInformation.liveChat"
            class="contact-icon-btn"
            id="juphyLiveChatButton"
            title="Live Chat"
            @click="handleLiveChatClick"
          >
            <img
              :src="require('@/img/socialSvg/liveChat-round.svg')"
              width="18"
              height="18"
              alt="Live Chat"
            />
          </button>
          
          <!-- Email Icon -->
          <button
            v-if="botConfig.contactInformation.email"
            class="contact-icon-btn"
            title="Email"
            @click="openEmail(botConfig.contactInformation.email)"
          >
            <img
              :src="require('@/img/socialSvg/send-email.svg')"
              width="18"
              height="18"
              alt="Email"
            />
          </button>
          
          <!-- Phone Icon -->
          <button
            v-if="botConfig.contactInformation.phone"
            class="contact-icon-btn"
            title="Phone"
            @click="openPhone(botConfig.contactInformation.phone)"
          >
            <img
              :src="require('@/img/socialSvg/phone-outlined.svg')"
              width="18"
              height="18"
              alt="Phone"
            />
          </button>
          
          <!-- WhatsApp Icon -->
          <button
            v-if="botConfig.contactInformation.whatsapp"
            class="contact-icon-btn"
            title="WhatsApp"
            @click="openWhatsApp(botConfig.contactInformation.whatsapp)"
          >
            <img
              :src="require('@/img/socialSvg/whatsApp-outlined.svg')"
              width="18"
              height="18"
              alt="WhatsApp"
            />
          </button>
        </div>

        <!-- Powered by Juphy Link -->
        <a
          v-if="!onRetrain && botConfig.hasJuphyBranding && !extension"
          class="powered-by-link"
          target="_blank"
          href="https://apps.shopify.com/juphy?utm_source=widget"
          :style="poweredByJuphyColor"
        >
          Powered by Juphy
        </a>

        <div
          v-if="!onRetrain && website?.pageId === '6808bd64a131628fb86b732c'"
          :style="poweredByJuphyColor"
          style="padding: 0.5rem; display: block; opacity: 0.35"
        ></div>

        <a
          v-if="!onRetrain && website?.pageId === '67a84f9693d3c43ed3e0d6a0'"
          href="#"
          :style="poweredByJuphyColor"
          style="
            padding: 0.5rem;
            display: block;
            opacity: 0.35;
            text-decoration: none;
            font-size: 13px;
            font-family: Arial, Helvetica, sans-serif;
          "
        >
          AI powered. Expert recommended.
        </a>
        <!--           Acik tonlar icin background color: rgba(0,0,0,0.1); color: rgba(0,0,0,0.8);
          koyu renkler icin background color: rgba(255,255,255,0.3); color: rgba(255,255,255,0.9); -->
      </div>
    </div>
  </div>
  <div
    v-if="messages[1]?.actionButton && website?.pageId === '67a84f9693d3c43ed3e0d6a0' && currentUrl?.includes('/glowchat')"
    class="fullscreen-overlay"
    style="width: 100vw; overflow: hidden"
  >
    <!-- Title and Subtitle -->
    <div style="text-align: center; margin-top: 1rem">
      <h3 class="numbercHeader">Meet GlowChat.AI</h3>
    </div>

    <!-- Main Image with Hearts -->
    <div class="juphy-fullscreen-image-container">
      <img
        src="https://cdn.shopify.com/s/files/1/0642/1299/2252/files/GlowChat-AI-Banner.png"
        alt="GlowChat AI"
        style="width: min(500px, 90vw); height: auto"
      />
    </div>

    <!-- Description -->
    <h4 class="numbercSubHeader">Simplify your beauty routine with AI-powered insights ✨</h4>
    <!--     <p style="color: #414f72; font-size: 18px; font-weight: 500; text-align: center; padding: 0 1rem">
      Let GlowChat.AI decode your skin's needs and set you up with the perfect match.
    </p> -->

    <!-- Quick Message Buttons -->
    <div style="position: absolute; bottom: 100px; left: 0; right: 0; padding: 0 1rem">
      <div
        style="
          display: flex;
          gap: 0.9rem;
          width: 100%;
          overflow-x: auto;
          white-space: nowrap;
          -webkit-overflow-scrolling: touch;
          padding-bottom: 10px;
          align-items: flex-end;
        "
      >
        <button
          v-for="actionButton in filteredActionButtons"
          :key="actionButton.text"
          @click="sendSuggestion(actionButton.text)"
          style="
            background: #fff;
            border: 1.5px solid #414f72;
            margin-left: 1px;
            color: #414f72;
            padding: 0.5rem 1rem;
            border-radius: 5px;
            cursor: pointer;
            flex: 0 0 auto;
            transition: all 0.2s ease;
            max-width: 300px;
            white-space: normal;
            text-align: left;
            word-wrap: break-word;
            line-height: 1.2;
            height: fit-content;
          "
          onmouseover="this.style.background='#fff5f5'; this.style.transform='scale(1.01)'; this.style.boxShadow='0 1px 4px rgba(211, 47, 47, 0.1)'"
          onmouseout="this.style.background='#fff'; this.style.transform='scale(1)'; this.style.boxShadow='none'"
        >
          {{ actionButton.text }}
        </button>
      </div>
    </div>
  </div>
</template>

<style>
.numbercHeader {
  color: #414f72;
  font-size: 28px;
  margin: 0;
  background: linear-gradient(90deg, #af64e5, #8180eb, #00b3f4);
  background-clip: border-box;
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}
.numbercSubHeader {
  color: #8180eb;
  font-size: 20px;
  width: fit-content;
  font-weight: 600;
  text-align: center;
  margin: 0 0 0.5rem 0;
  padding: 0 1rem;
}

p {
  margin: 0;
  padding: 0;
}

ol {
  margin: 0.5rem 0;
  padding-left: 1rem;
}

ul {
  margin: 0.5rem 0;
  padding-left: 1rem;
}

li {
  margin-bottom: 0.1rem;
}

/* Add specific override for the given page ID */
[data-page-id='67a84f9693d3c43ed3e0d6a0'] ul li span a {
  color: #555555 !important;
}

#wcw_input {
  -webkit-backdrop-filter: blur(10px);
  backdrop-filter: blur(10px);
}

@keyframes slideInLeft {
  from {
    transform: translateX(-100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

@keyframes slideInRight {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

.fullscreen-overlay {
  position: absolute;
  top: 76px;
  left: 0;
  width: 100%;
  height: calc(100vh - 76px);
  background: #ffffff;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow-y: auto;
  font-family: 'Roboto', sans-serif;
}

.juphy-fullscreen-image-container {
  position: relative;
  width: 100%;
  text-align: center;
  margin: 1.5rem 0 0.7rem 0;
}

.juphy-fullscreen-image-container::after {
  content: '';
  position: absolute;
  inset: 0;
  background: none;
  background-size: auto;
  background-size: contain;
}

.fullscreen-overlay::before {
  content: '';
  position: absolute;
  inset: 0;
  background: url(https://cdn.shopify.com/s/files/1/0642/1299/2252/files/GlowAI-BG-Img-2Circle.png?v=1746007064);
  background-size: auto;
  background-size: cover;
}

.clearfix.bot {
  animation: none;
}

.clearfix.agent {
  animation: slideInRight 0.4s ease forwards;
}

.clearfix.log {
  animation: slideInRight 0.4s ease forwards;
}

.clearfix.bot.typing-indicator {
  animation: slideInLeft 0.4s ease forwards;
}

.dot {
  opacity: 0.1; /* Start with dots hidden */
  animation: blink 1.7s infinite; /* Apply the blink animation */
}

/* Apply different animation delays to each dot to animate in order */
.dot:nth-child(1) {
  animation-delay: 0s;
}
.dot:nth-child(2) {
  animation-delay: 0.33s;
}
.dot:nth-child(3) {
  animation-delay: 0.66s;
}

@keyframes blink {
  0%,
  100% {
    opacity: 0;
  }
  50% {
    opacity: 1;
  }
}

::placeholder {
  color: #72777a;
  opacity: 0.7;
  font-size: 1rem;
}

:-ms-input-placeholder {
  /* Internet Explorer 10-11 */
  color: #72777a;
  opacity: 0.7;
}

::-ms-input-placeholder {
  /* Microsoft Edge */
  color: #72777a;
  opacity: 0.7;
}

input {
  outline: none;
}

.guideContainer {
  background: rgba(0, 0, 0, 0);
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
}

.WebChatwidget {
  height: 100vh;
  box-sizing: border-box;
  border: 0 solid #e5e7eb;
  box-shadow: 2px 2px 20px rgba(0, 0, 0, 0.1);
  background: #ffffff;
  color: rgba(0, 0, 0, 0.7);
  right: 1rem;
  bottom: 1rem;
  z-index: 9999999;
  overflow-x: hidden;
}

/* Extension specific styles */
.WebChatwidget[data-extension='true'] {
  box-shadow: none;
  height: 100vh;
}

.WebChatwidget[data-extension='true'] .wcw_header {
  height: auto;
}

.WebChatwidget[data-extension='true'] .wcw_header .content {
  padding: 0.75rem;
}

.WebChatwidget[data-extension='true'] .wcw_header .agents {
  width: 2.25rem;
  height: 2.25rem;
}

.WebChatwidget[data-extension='true'] .wcw_header .status .label .widgetName {
  font-size: 0.875rem;
}

.WebChatwidget[data-extension='true'] .wcw_header .status .label .widgetStatus {
  font-size: 0.75rem;
}

.WebChatwidget[data-extension='true'] .wcw_messages_container {
  margin-bottom: -60px;
  padding-bottom: 60px;
}

.WebChatwidget[data-extension='true'] #wcw_messages {
  padding: 0 0.75rem;
}

.WebChatwidget[data-extension='true'] #wcw_messages > li > .content {
  font-size: 0.875rem;
  line-height: 1.25rem;
  padding: 8px 12px;
}

.WebChatwidget[data-extension='true'] .richMsgContainer {
  width: 200px;
}

.WebChatwidget[data-extension='true'] .richMsgImg {
  height: 160px;
  min-width: 120px;
}

.WebChatwidget[data-extension='true'] .richMsgTitle {
  font-size: 0.875rem;
  line-height: 1.25rem;
  margin-bottom: 0.75rem;
}

.WebChatwidget[data-extension='true'] .richMsgDesc {
  font-size: 0.875rem;
  line-height: 1.25rem;
}

.WebChatwidget[data-extension='true'] .richMsgButton > .button2 {
  padding: 8px 12px;
  font-size: 0.875rem;
}

.WebChatwidget[data-extension='true'] form {
  padding: 0.75rem;
}

.WebChatwidget[data-extension='true'] form > input {
  height: 45px;
  font-size: 16px;
}

.WebChatwidget[data-extension='true'] form > input::placeholder {
  font-size: 16px;
}

.WebChatwidget[data-extension='true'] #wcw_form_button {
  width: 39px;
  height: 39px;
  top: calc(50%);
}

.WebChatwidget[data-extension='true'] .juphy-button {
  padding: 8px 12px !important;
  font-size: 0.875rem !important;
  margin-bottom: 8px !important;
}

.WebChatwidget[data-extension='true'] .wcw_close {
  display: none !important;
}

.wcw_wrapper {
  display: flex;
  max-height: 100%;
  flex-direction: column;
  position: relative;
}

.wcw_wrapper .wcw_header {
  height: 100%;
  background: #ffffff;
}

.wcw_wrapper .wcw_header .content {
  display: flex;
  position: relative;
  padding: 1rem;
  padding-top: 0.5rem;
  background-color: #f6f6f6;
}

.retrainButton {
  padding: 5px 20px;
  display: inline-block !important;
  text-decoration: underline;
  font-weight: bold;
  font-family: arial;
  margin: 0;
}

.retrainButton:hover {
  cursor: pointer;
  text-decoration: underline;
}

/* .hidden {
  display: none !important;
} */

.wcw_wrapper .wcw_header .agents {
  margin-right: 8px;
  width: 2.75rem;
  height: 2.75rem;
}

.wcw_wrapper .wcw_header .status {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.wcw_wrapper .wcw_header .status .label {
  display: flex;
  border-radius: 50px;
  align-items: center;
  height: 20px;
  align-items: center;
}
.wcw_wrapper .wcw_header .status .label div {
  width: 8px;
  height: 8px;
  margin-right: 4px;
  top: 10px;
}

.wcw_wrapper .wcw_header .status .label .widgetName {
  color: #202325;
  width: inherit;
  height: 1rem;
  font-family: Helvetica;
  font-size: 1rem;
  font-weight: bold;
  line-height: 20px;
}

.wcw_wrapper .wcw_header .status .label .widgetStatus {
  width: inherit;
  height: 1rem;
  color: #72777a;
  font-family: Arial, Helvetica, sans-serif;
  font-size: 13px;
  line-height: 20px;
}

.wcw_wrapper .wcw_header .status .label .indicator {
  height: 0.5rem;
  width: 0.5rem;
  background: #7dde86;
  border-radius: 8px;
  display: block;
  margin-right: 5px;
}

.wcw_wrapper .wcw_header .content img {
  width: 44px;
  height: 44px;
  border-radius: 40px;
  right: 0;
}

.wcw_wrapper .wcw_header .content .wcw_close {
  position: absolute;
  width: 2rem;
  height: 2rem;
  transition: 0.5s;
  margin-right: 1rem;
  padding: 4px;
  right: 0;
  border-color: #e3e5e5;
  border-radius: 2rem;
  display: flex;
  justify-content: center;
  flex-direction: column;
  z-index: 2;
}

.wcw_wrapper .wcw_header .content .wcw_close .svg {
  width: 1.5rem;
  height: 1.5rem;
}

.wcw_wrapper .wcw_header .content:hover .wcw_close {
  opacity: 1;
}

.wcw_wrapper .wcw_header .content .wcw_close:hover {
  background: rgba(0, 0, 0, 0.05);
  cursor: pointer;
}

.wcw_wrapper .wcw_messages_container {
  overflow-y: auto;
  overflow-x: hidden;
  height: 100vh;
  scrollbar-width: none;
  -ms-overflow-style: none;

  /* Premium */
  margin-bottom: -76px;
  padding-bottom: 76px;

  /* height: 500px; */
}

.wcw_wrapper .wcw_messages_container::-webkit-scrollbar {
  display: none;
}

.wcw_wrapper .order input {
  height: 2.75rem;
  width: 100%;
  padding: 5px 10px 5px 10px;
  margin: auto;
  flex-grow: 1;
  max-height: 100%;
  border-radius: 0.2rem;
  font-size: 1rem;
  border: 1.5px solid;
  border-color: #979c9e;
  margin-bottom: 0.5rem;
  box-sizing: border-box;
}

.wcw_wrapper .order button {
  background: transparent;
  border-style: none;
  cursor: pointer;
  width: fit-content;
  border-radius: 24px;
  width: 100%;
  cursor: pointer;
  padding: 8px 0;
  border: none;
  background-color: rgba(210, 228, 254, 0.29);
  color: rgba(58, 86, 140, 0.9);
  font-size: 0.875rem;
  line-height: 1.5rem;
}

.wcw_wrapper form {
  justify-content: space-between;
  width: inherit;
  padding: 1rem;
  background-color: #f6f6f6;
  display: flex;
  margin: 0;
  padding-bottom: 0px;
}

.wcw_wrapper form.contact-info-active {
  margin-bottom: 0.5rem;
}

/* Widget Footer Styles */
.widget-footer {
  display: block;
  text-align: center;
}

/* When contact icons are present, use flex layout */
.widget-footer:has(.contact-icons-container) {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 1rem;
}

.contact-icons-container {
  display: flex;
  align-items: center;
  gap: 6px;
}

.contact-icon-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.06);
  cursor: pointer;
  transition: all 0.2s ease;
  opacity: 0.7;
}

.contact-icon-btn:hover {
  background: rgba(0, 0, 0, 0.12);
  opacity: 1;
  transform: translateY(-1px);
}

.contact-icon-btn:active {
  transform: translateY(0);
}

.contact-icon-btn img {
  display: block;
  opacity: 0.75;
  transition: opacity 0.2s ease;
}

.contact-icon-btn:hover img {
  opacity: 1;
}

/* Default powered-by link (no contact info) - matches original styling */
.powered-by-link {
  padding: 0.5rem;
  opacity: 0.35;
  text-decoration: none;
  font-size: 13px;
  font-family: Arial, Helvetica, sans-serif;
  display: inline-block;
}

/* When contact icons are present, adjust powered-by link */
.widget-footer:has(.contact-icons-container) .powered-by-link {
  padding: 0.25rem 0.5rem;
  font-size: 12px;
}

.wcw_wrapper form > input {
  position: sticky;
  bottom: 0;
  padding: 10px 20px 10px 20px;
  margin: auto;
  flex-grow: 1;
  max-height: 100%;
  border-radius: 30px;
  font-size: 1rem;
  border: none;
  box-sizing: border-box;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 15%);
  padding-right: 55px;
  width: calc(100% - 20px);
  height: 60px;
}

.wcw_wrapper form > input:focus {
  outline: none;
}

#wcw_form_button {
  position: absolute;
  right: 24px;
  transform: translateY(-50%);
  z-index: 3;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: calc(50% + 8px);
  transform: translateY(-50%);
}

#wcw_form_button img {
  width: 20px;
  height: 20px;
}

.wcw_wrapper #wcw_messages {
  list-style-type: none;
  margin: 0;
  padding: 0 1rem 0 1rem;
  align-items: center;
}

.wcw_wrapper #wcw_messages > li {
  margin-bottom: 1rem;
  display: block;
}
.wcw_wrapper #wcw_messages > li.clearfix.bot {
  display: flex;
  width: fit-content;
  justify-content: flex-start;
  flex-direction: column;
}

.wcw_wrapper #wcw_messages > li:last-child {
  margin-bottom: 1rem;
  display: block;
}

.wcw_wrapper #wcw_messages > li.quick-answers-li {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 1rem;
}

.wcw_wrapper #wcw_messages > li:first-child {
  margin-top: 1rem;
}

.clearfix::after {
  content: '';
  clear: both;
  display: table;
}

.wcw_wrapper #wcw_messages > li > .content {
  font-family: Arial, Helvetica, sans-serif;
  font-size: 1rem;
  line-height: 24px;
  padding: 10px 1rem;
  background: #f2f4f5;
  color: #303437;
  border-radius: 0px 24px 24px 24px;
  word-wrap: break-word;
  width: fit-content;
  max-width: 90%;
}

.wcw_wrapper #wcw_messages > li > .content li {
  white-space: normal;
  word-break: break-word;
}

/* Ensure proper wrapping for long URLs or text within list items */
.wcw_wrapper #wcw_messages > li > .content li a {
  word-break: break-all;
  display: inline-block;
}

.wcw_wrapper #wcw_messages > li > .content img {
  width: 100%;
}

.wcw_wrapper #wcw_messages > li > img {
  width: 2rem;
  height: 2rem;
  align-self: flex-start;
  margin-right: 10px;
}

.wcw_wrapper #wcw_messages > li > .content.agent {
  padding: 0.5rem 1rem;
  color: #ffffff;
  background: #0070f0;
  border-radius: 24px 24px 0px 24px;
  float: right;
  max-width: 90%;
}

.wcw_wrapper #wcw_messages > li > .content.email {
  padding: 1rem;
  background: white;
  border-radius: 1rem;
  float: right;
  max-width: 70%;
}

.link-preview {
  display: flex;
  border: 1px solid rgba(0, 0, 0, 0.1);
  margin: 0.5rem 0;
  padding: 0.5rem;
  border-radius: 8px;
  background-color: #f5f5f5;
}

.preview-image {
  max-height: 100%;
  height: 80px;
  width: 100%;
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  border-radius: 8px;
  margin-bottom: 2px;
}

.preview-content {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  padding-left: 0.5rem;
}

.preview-title {
  font-weight: bold;
  margin-bottom: 0.25rem;
  font-size: medium;
  color: rgba(0, 0, 0, 0.7);
}

.preview-description {
  color: rgba(0, 0, 0, 0.7);
  font-size: small;
}

.form-container {
  display: flex;
  flex-direction: column;
  position: relative;
  background-color: white;
  color: rgba(0, 0, 0, 0.7);
  border-radius: 10px;
  padding: 20px;
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.1);
  width: fit-content;
  float: right;
}

.form-container input[type='email'] {
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 5px 0px 0px 5px;
  width: 100%;
  outline: none;
}
.form-container input[type='email']::placeholder {
  opacity: 0.3;
}

.form-container button {
  padding: 5px 20px;
  background-color: #4169e1;
  border: none;
  border-radius: 0px 5px 5px 0px;
  color: white;
  cursor: pointer;
  font-size: 1.5rem;
  position: relative;
  overflow: hidden;
}

.form-container button::before {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 1.5rem;
  line-height: 1;
}

.get-notified-text {
  font-size: 1.2rem;
  margin-bottom: 10px;
}

.input-group {
  display: flex;
}

.st0 {
  fill: none;
}

.buttonGroup {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 10px;
}

.optionsButton {
  background-color: #e5eeff;
  color: #003499;
  padding: 10px 20px;
  border-radius: 6px;
  margin: 5px;
  cursor: pointer;
  transition: background-color 0.3s, color 0.3s;
  font-size: 1rem;
  line-height: 18px;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: center;
  align-items: center;
  max-width: 100%;
  white-space: nowrap;
}

.optionsButton:hover {
  color: #e5eeff;
  background-color: #003499;
  transition: all 0.5s;
}

.richMsgContainer {
  flex: 0 0 auto;
  width: 245px;
  height: fit-content;
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  position: relative;
}

.richMsgContainer:last-child {
  margin-right: 10px;
}

.richMsg-wrapper {
  position: relative;
  margin-left: -1rem;
}

.richMsg {
  display: flex;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior-x: contain;
  max-width: 340px;
  scroll-behavior: smooth;
  position: relative;
  gap: 12px;
  padding: 4px 0 8px 0;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.richMsg:not(.order) {
  margin-left: 0;
}

.carousel-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-60%);
  z-index: 2;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.12), 0 0 0 0.5px rgba(0, 0, 0, 0.06);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  transition: opacity 0.2s ease, transform 0.15s ease, box-shadow 0.2s ease;
  opacity: 1;
}

.carousel-arrow:hover {
  background: rgba(255, 255, 255, 0.97);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18), 0 0 0 0.5px rgba(0, 0, 0, 0.08);
  transform: translateY(-60%) scale(1.06);
}

.carousel-arrow:active {
  transform: translateY(-60%) scale(0.95);
}

.carousel-arrow.hidden {
  opacity: 0;
  pointer-events: none;
}

.carousel-arrow svg {
  width: 16px;
  height: 16px;
}

.carousel-arrow-left {
  left: 6px;
}

.carousel-arrow-right {
  right: 6px;
}

@media (hover: none) and (pointer: coarse) {
  .carousel-arrow {
    display: none;
  }
}

/* Add specific override for the given page ID */
[data-page-id='67a84f9693d3c43ed3e0d6a0'] .richMsg {
  scrollbar-width: none;
  -ms-overflow-style: none;
}

/* Apply max-width override only on desktop screens */
@media screen and (min-width: 768px) {
  [data-page-id='67a84f9693d3c43ed3e0d6a0'] .richMsg {
    max-width: inherit !important;
    padding-top: 1rem;
  }
}

.richMsg::-webkit-scrollbar {
  display: none;
}

.richMsgContainer:last-child {
  margin-right: 10px;
}

.wcw_wrapper #wcw_messages > li > .content.richMsg {
  display: flex;
  flex-direction: column;
  border-radius: 0px 24px 24px 24px;
  line-height: 10px;
  background: #f2f4f5;
  padding: 0;
  position: relative;
}

/* .richMsgImg img {
  max-width: 100%;
  height: 8.125rem;
  border-radius: 10px;
  margin-top: 0.9rem;
  width: 100%;
} */

.richMsgImg {
  margin: 0;
  width: 100%;
  height: 211px;
  background-position: center !important;
  background-size: cover !important;
  border-radius: 12px 12px 0 0;
  object-fit: cover;
}

.wcw_wrapper #wcw_messages > li > .content > .button {
  padding: 5px 7px;
  background-color: var(--juphy-chatbot-background-color);
  color: #003499;
  border-radius: 0.5rem;
  margin: 5px;
  display: inline-block;
  font-size: 1rem;
  cursor: pointer;
}

.wcw_wrapper #wcw_messages > li > .content > .button:hover {
  color: #e5eeff;
  background-color: #003499;
  transition: all 0.5s;
}

.richMsgHeader {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 12px;
  text-align: center;
}

.richMsgTitle {
  font-family: Arial, Helvetica, sans-serif;
  font-size: 0.95rem;
  line-height: 1.2;
  color: #303437;
  font-weight: 600;
  opacity: 90%;
  margin: 0;
  width: 100%;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: normal;
}

.richMsgPrice {
  font-weight: 600;
  color: #303437;
  font-size: 1rem;
  line-height: 1.2;
  display: flex;
  align-items: center;
  gap: 6px;
  justify-content: center;
}

.richMsgPrice.has-svg-icon {
  gap: 3px;
}

.richMsgPrice svg {
  width: 0.85em;
  height: 0.85em;
  vertical-align: middle;
  margin-top: -4px;
  margin-right: 2px;
}

.originalPrice {
  text-decoration: line-through;
  color: #666;
  font-weight: normal;
  font-size: 0.9rem;
}

.originalPrice svg {
  width: 0.75em;
  height: 0.75em;
  margin-top: -3px;
  margin-right: 2px;
}

.discountBadge {
  background: #ff4646;
  color: white;
  padding: 3px 6px;
  border-radius: 4px;
  font-size: 0.8rem;
  font-weight: 600;
}

.wcw_init {
  overflow: visible;
  position: fixed;
  right: 1rem;
  bottom: 1rem;
  box-shadow: rgba(0, 18, 46, 0.16) 0px 8px 22px 0px;
  border-radius: 8px;
  background: #ffffff;
  color: rgba(0, 0, 0, 0.7);
}

.wcw_init .wcw_classic {
  padding: 8px 12px;
  cursor: pointer;
}

.richMsgButton {
  text-align: center;
  width: 100%;
  display: flex;
  gap: 0;
}

.richMsgButton > .button2 {
  border-radius: 2px;
  width: 100%;
  cursor: pointer;
  border: none;
  background-color: #2c3338;
  color: #fff;
  font-size: 0.95rem;
  line-height: 1.2;
  display: inline-block;
  text-align: center;
  text-decoration: none;
  transition: opacity 0.2s ease;
  font-weight: 500;
  position: relative;
}

.richMsgButton > .button2:hover {
  opacity: 0.9;
}

.richMsgButton > .button2::after {
  content: '';
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  width: 1px;
  background: rgba(255, 255, 255, 0.15);
  pointer-events: none;
}

.richMsgButton button {
  font-size: 0.95rem !important;
  line-height: 1.2 !important;
  font-weight: 500 !important;
  transition: opacity 0.2s ease;
  position: relative;
}

.richMsgButton button::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 1px;
  background: rgba(0, 0, 0, 0.15);
  pointer-events: none;
}

.richMsgButton button:hover:not(:disabled) {
  opacity: 0.9;
}

.richMsgFooter {
  padding: 12px;
  background: #fff;
  margin-top: auto;
}

.richMsgHeader {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  gap: 8px;
  min-height: 24px;
}

.richMsgPrice {
  font-weight: 600;
  color: #303437;
  white-space: nowrap;
  font-size: 0.95rem;
  line-height: 1.2;
  display: flex;
  align-items: center;
  gap: 6px;
}

.richMsgPrice.has-svg-icon {
  gap: 3px;
}

.richMsgPrice svg {
  width: 0.85em;
  height: 0.85em;
  vertical-align: middle;
  margin-top: -4px;
  margin-right: 2px;
}

.originalPrice {
  text-decoration: line-through;
  color: #666;
  font-weight: normal;
  font-size: 0.85rem;
}

.originalPrice svg {
  width: 0.75em;
  height: 0.75em;
  margin-top: -3px;
  margin-right: 2px;
}

.discountBadge {
  background: #ff4646;
  color: white;
  padding: 2px 4px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
}

.quickActionButton {
  color: black;
  margin: 0.5rem;
  border-radius: 0.75rem;
  padding: 0.1rem 1rem;
  display: inline-block;
  background-color: #e5e7eb;
  white-space: nowrap;
  height: 36px;
  border: 0 solid #e5e7eb;
  width: max-content;
  cursor: pointer;
  font-family: ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica Neue, Arial,
    Noto Sans, sans-serif, Apple Color Emoji, Segoe UI Emoji, Segoe UI Symbol, Noto Color Emoji;
}

.quickActionButton:hover {
  background-color: #d1d5db;
}

.quickActionButton:disabled {
  color: #9ca3af;
  background-color: #f3f4f6;
  cursor: not-allowed;
}

.quickActionButton:disabled:hover {
  background-color: #f3f4f6;
}

.juphy-button {
  border-radius: 24px !important;
  padding: 10px 1rem !important;
  cursor: pointer !important;
  font-size: 1rem !important;
  transition: background 0.3s, box-shadow 0.3s, opacity 0.3s !important;
  text-align: center !important;
  margin-bottom: 10px !important;
  margin-right: 3px !important;
  white-space: normal !important;
  word-break: break-word;
  max-width: 100%;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.15);
}

.juphy-button:hover {
  opacity: 0.8;
}

@keyframes sendAnimation {
  0% {
    transform: translateX(0);
    opacity: 1;
  }
  35% {
    transform: translateX(20px);
    opacity: 0;
  }
  36% {
    transform: translateX(-20px);
    opacity: 0;
  }
  80% {
    transform: translateX(4px);
    opacity: 1;
  }
  100% {
    transform: translateX(0);
    opacity: 1;
  }
}

#wcw_form_button img {
  width: 20px;
  height: 20px;
}

#wcw_form_button.animate img {
  animation: sendAnimation 0.7s cubic-bezier(0.25, 0.1, 0.25, 1) forwards;
}

.spinner {
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 2px solid #eee;
  border-top: 2px solid #333;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
  vertical-align: middle;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.added-checkmark {
  color: #27ae60;
  font-size: 1.2em;
  margin-right: 4px;
  vertical-align: middle;
}
.added-text {
  font-weight: 600;
  vertical-align: middle;
}

.hide-status {
  opacity: 0 !important;
}

.white-background {
  background: #fff !important;
}

.custom-number-c-close {
  padding: 4px;
  background: rgba(0, 0, 0, 0.05) !important;
  transition: background-color 0.2s ease, opacity 0.2s ease;
  border-radius: 4px;
}

.custom-number-c-close:hover {
  background: rgba(0, 0, 0, 0.12) !important;
  opacity: 0.8;
  cursor: pointer;
}

.richMsgFooter {
  padding: 12px;
  background: #fff;
}

.richMsgHeader {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
}

.richMsgPrice {
  font-weight: 600;
  color: #303437;
  white-space: nowrap;
}

.button-hover:hover {
  opacity: 0.9;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15) !important;
}

.WebChatwidget[data-extension='true'] .richMsg {
  max-width: 280px;
  padding: 0.75rem !important;
}

.WebChatwidget[data-extension='true'] .richMsg.order {
  width: 100%;
  max-width: 260px;
}

.WebChatwidget[data-extension='true'] .richMsg.order input {
  height: 32px;
  padding: 4px 8px;
  margin-bottom: 0.35rem;
  font-size: 0.875rem;
}

.WebChatwidget[data-extension='true'] .richMsg.order button {
  padding: 6px 0;
  font-size: 0.875rem;
}

.WebChatwidget[data-extension='true'] .richMsgContainer {
  width: 180px;
}

.WebChatwidget[data-extension='true'] .richMsgImg {
  height: 140px;
  min-width: 100px;
  margin-bottom: 0.75rem;
}

.WebChatwidget[data-extension='true'] .richMsgButton {
  max-width: 180px;
}

.WebChatwidget[data-extension='true'] .richMsgButton > .button2 {
  padding: 6px 10px;
  font-size: 0.75rem;
}

.WebChatwidget[data-extension='true'] .carousel-arrow {
  width: 22px;
  height: 22px;
}

.WebChatwidget[data-extension='true'] .carousel-arrow svg {
  width: 12px;
  height: 12px;
}

.no-branding-margin {
  margin-bottom: 1rem !important;
}

/* Mobile draggable indicator */
.mobile-drag-indicator {
  display: none;
  padding: 5px 0 2px 0;
  cursor: pointer;
  justify-content: center;
  align-items: center;
  position: relative;
  z-index: 10;
  border-radius: 0;
}

.drag-line {
  width: 36px;
  height: 4px;
  background: rgba(255, 255, 255, 0.3);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  border-radius: 3px;
  transition: all 0.2s ease;
}

.mobile-drag-indicator:hover .drag-line {
  background: rgba(255, 255, 255, 0.5);
  transform: scaleX(1.15);
}

.mobile-drag-indicator:active .drag-line {
  width: 42px;
  background: rgba(255, 255, 255, 0.6);
  transform: scaleX(1.05);
}

/* Mobile specific styles */
@media screen and (max-width: 768px) {
  .mobile-drag-indicator {
    display: flex;
  }

  .WebChatwidget {
    position: fixed !important;
    bottom: 0 !important;
    left: 0 !important;
    right: 0 !important;
    height: 100vh !important;
    transition: height 0.3s ease;
  }

  .WebChatwidget.mobile-75-height {
    height: 75vh !important;
  }

  /* Make header clickable with visual feedback */
  .wcw_header .content {
    cursor: pointer;
    transition: background-color 0.2s ease;
  }

  /* Disable drag toggle when in juphy mode */
  .wcw_header .content.no-drag-toggle {
    cursor: default;
  }
}

/* Disambiguation quick answer buttons - right-aligned reply style */
.quick-answers-container {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
  margin-top: 1rem;
  width: 100%;
  align-self: stretch;
}

.quick-answers-container .quick-answer-btn:nth-child(1) { animation: qaFadeIn 0.25s ease-out both; animation-delay: 0.05s; }
.quick-answers-container .quick-answer-btn:nth-child(2) { animation: qaFadeIn 0.25s ease-out both; animation-delay: 0.1s; }
.quick-answers-container .quick-answer-btn:nth-child(3) { animation: qaFadeIn 0.25s ease-out both; animation-delay: 0.15s; }
.quick-answers-container .quick-answer-btn:nth-child(4) { animation: qaFadeIn 0.25s ease-out both; animation-delay: 0.2s; }

@keyframes qaFadeIn {
  from {
    opacity: 0;
    transform: translateX(12px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.quick-answer-btn {
  background: transparent;
  border: 1.5px solid var(--qa-color, #0070f0);
  color: var(--qa-color, #0070f0);
  padding: 8px 16px;
  border-radius: 24px 24px 4px 24px;
  font-family: Arial, Helvetica, sans-serif;
  font-size: 0.9rem;
  line-height: 1.4;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease, box-shadow 0.2s ease, transform 0.15s ease;
  white-space: normal;
  text-align: right;
  word-wrap: break-word;
  max-width: 80%;
  font-weight: 400;
}

.quick-answer-btn:hover {
  background: var(--qa-color, #0070f0);
  color: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transform: translateY(-1px);
}

.quick-answer-btn:active {
  transform: scale(0.97);
  box-shadow: none;
}

/* MultipleChoice buttons container - outside the bubble */
.mc-buttons-container {
  margin-top: 8px;
  width: 100%;
  max-width: 90%;
}

/* Pending attachments preview - minimal style */
.pending-attachments-preview {
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.pending-attachments-preview::-webkit-scrollbar {
  display: none;
}
</style>
